import { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import { Link } from 'react-router-dom';
import { Search, ShoppingCart, Plus, Minus, Trash2, X, FileText, DollarSign, Lock, Unlock, HelpCircle, Info, TrendingUp, ArrowLeft, Ticket, Package, Printer, Wallet, ArrowDownToLine, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { isCajeroUser, canManageVendedorCodigos } from '../utils/userRoles';
import { resolveTotalVentasReales } from '../utils/ventasReales';
import { useIdleTimeout } from '../hooks/useIdleTimeout';
import DenominacionEfectivo from '../components/DenominacionEfectivo';
import {
    createEmptyDesglose,
    compactDesglose,
    parseMontoClp,
    validateDesgloseVsMonto,
} from '../utils/denominacionesClp';

const ROLE_ADMIN = 4;
const ROLE_PREVENTA = 1;
const ROLE_VENDEDOR = 2;

/** Cobro informado en ticket cuando el cliente no trae envase retornable (solo comprobante impreso). */
const ENVASE_TICKET_MONTO_CLP = 500;

const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const IDLE_WARN_MS = 60 * 1000;

export default function POSPage() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const searchInputRef = useRef(null);
    const hasAutoShownOpenCajaRef = useRef(false);

    // Core State
    const [products, setProducts] = useState([]);
    const [cart, setCart] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    
    // Toast notification for barcode not found
    const [toast, setToast] = useState({ show: false, message: '', type: 'error' });

    // POS Feature State
    const [cajaSession, setCajaSession] = useState(null); // { isOpen: bool, session: {} }
    const [availableCajas, setAvailableCajas] = useState([]);
    const [preventas, setPreventas] = useState([]);
    const [activePreventaId, setActivePreventaId] = useState(null);
    const [cajaStatusLoading, setCajaStatusLoading] = useState(false); // Para evitar mostrar modal mientras se carga

    // Modals
    const [showPreventaModal, setShowPreventaModal] = useState(false); // List of pending preventas
    const [showPromocionesModal, setShowPromocionesModal] = useState(false);
    const [promotions, setPromotions] = useState([]);
    const [promoSearchTerm, setPromoSearchTerm] = useState('');
    const [promoSelectionModal, setPromoSelectionModal] = useState(null); // { promo, detail }
    const [promotionsInSearch, setPromotionsInSearch] = useState([]);
    const [showOpenCajaModal, setShowOpenCajaModal] = useState(false);
    const [showCloseCajaModal, setShowCloseCajaModal] = useState(false);
    const [showPesoModal, setShowPesoModal] = useState(false);
    const [pesoProductToAdd, setPesoProductToAdd] = useState(null);
    const [pesoGramsInput, setPesoGramsInput] = useState('');
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentData, setPaymentData] = useState({ 
        efectivo: 0, 
        tarjeta_debito: 0, 
        tarjeta_credito: 0
    });
    /** Vuelto entregado desde caja (p. ej. cargo alto en tarjeta y efectivo al cliente); cuadra egreso en caja_efectivo_ajuste */
    const [vueltoEfectivo, setVueltoEfectivo] = useState(0);
    const [openCajaData, setOpenCajaData] = useState({ caja_id: '', monto_inicial: '' });
    const [openCajaDesglose, setOpenCajaDesglose] = useState(() => createEmptyDesglose());
    const [closeCajaData, setCloseCajaData] = useState({ efectivo_cierre: '' });
    const [closeCajaDesglose, setCloseCajaDesglose] = useState(() => createEmptyDesglose());
    const [closeCajaForzar, setCloseCajaForzar] = useState(false);
    const [cierreResumen, setCierreResumen] = useState(null);
    const [showPagoCuentaModal, setShowPagoCuentaModal] = useState(false);
    const [pagoCuentaModo, setPagoCuentaModo] = useState('efectivo'); // 'efectivo' | 'tarjeta_externa'
    const [pagoCuentaMonto, setPagoCuentaMonto] = useState('');
    const [pagoCuentaLoading, setPagoCuentaLoading] = useState(false);
    const [showMovimientoEfectivoModal, setShowMovimientoEfectivoModal] = useState(false);
    /** 'inyeccion_efectivo' | 'retiro_efectivo_seguridad' */
    const [movimientoEfectivoTipo, setMovimientoEfectivoTipo] = useState('inyeccion_efectivo');
    const [movimientoEfectivoMonto, setMovimientoEfectivoMonto] = useState('');
    const [movimientoEfectivoDesglose, setMovimientoEfectivoDesglose] = useState(() => createEmptyDesglose());
    /** Hint when opening injection from payment modal (insufficient cash for change). */
    const [movimientoEfectivoSugerido, setMovimientoEfectivoSugerido] = useState(null);
    const [movimientoEfectivoLoading, setMovimientoEfectivoLoading] = useState(false);

    /** Solo en flujo «Completar venta» (modal de pago): ajuste envase en total y boleta. */
    const [boletaCargoEnvase, setBoletaCargoEnvase] = useState(false);
    const [boletaDescuentoDevolucionEnvase, setBoletaDescuentoDevolucionEnvase] = useState(false);

    // Vendedor asignado por escaneo de código de barras (VND + id, ej. VND42)
    const [selectedVendedor, setSelectedVendedor] = useState(null);
    // Modal para preguntar vendedor al crear preventa
    const [showVendedorPreventaModal, setShowVendedorPreventaModal] = useState(false);
    const [vendedorPreventaInput, setVendedorPreventaInput] = useState('');
    const [vendedorPreventaResolved, setVendedorPreventaResolved] = useState(null); // { id, name }
    const vendedorPreventaInputRef = useRef(null);

    const isPreventaPrevendedor = Number(user?.role) === ROLE_PREVENTA;
    const isVendedorRole = Number(user?.role) === ROLE_VENDEDOR;
    const isAdmin = Number(user?.role) === ROLE_ADMIN;
    const isCajero = isCajeroUser(user);
    /** Solo Preventa y Admin generan preventas en POS (cajero usa venta directa o carga PRV). */
    const canCreatePreventa = isPreventaPrevendedor || isAdmin;
    const requiereCajaOperativa = isCajero || isAdmin;
    const canCompleteSale = requiereCajaOperativa;
    const canCompleteSaleWithLoadedPreventa = canCompleteSale || (isVendedorRole && activePreventaId);
    /** Cajero/admin con turno abierto pero caja o sucursal inactiva: no cobrar hasta corregir en administración. */
    const cajeroVenBloqueadoPorEstadoLocal =
        requiereCajaOperativa &&
        Boolean(cajaSession?.isOpen) &&
        cajaSession?.sales_blocked === true;
    /** Cajero o admin puede cobrar solo con caja abierta y operativa. */
    const puedeEjecutarCobro =
        requiereCajaOperativa &&
        Boolean(cajaSession?.isOpen) &&
        cajaSession?.sales_blocked !== true;
    /** Alias for cajero-specific UI (vuelto, envases) — same rule as admin now. */
    const cajeroPuedeEjecutarCobro = puedeEjecutarCobro;
    /** Total con envases en boleta / toggles POS que requieren caja válida. */
    const opcionesSesionCajaVentasActivas =
        canCompleteSale &&
        Boolean(cajaSession?.isOpen) &&
        cajaSession?.sales_blocked !== true;
    const canLoadPreventaInPos = !isPreventaPrevendedor;
    const canVendedorCodigos = canManageVendedorCodigos(user);
    /** Preventa/Vendedor pueden escanear VND para asignación; admin/jefe igual */
    const canScanVendedorBarcode = canVendedorCodigos || isPreventaPrevendedor || isVendedorRole || canCompleteSale;
    /** Cajero/Admin: VND opcional (sin escaneo se atribuye al cajero). Vendedor: obligatorio. */
    const saleRequiresVnd = isVendedorRole;
    const showVendedorAssignment = canCompleteSaleWithLoadedPreventa;

    useEffect(() => {
        if (user?.id != null) {
            fetchAvailableCajas();
        }
        if (user?.id != null) {
            fetchCajaStatus();
        }
    }, [user?.id]);

    useEffect(() => {
        fetchPromotions();
    }, []);

    useEffect(() => {
        if (isCajero && searchParams.get('openCaja') === '1' && !cajaSession?.isOpen) {
            setShowOpenCajaModal(true);
            setSearchParams({}, { replace: true });
        }
    }, [isCajero, searchParams, cajaSession?.isOpen, setSearchParams]);

    // Mostrar modal de abrir caja automáticamente solo para CAJEROS que no tienen caja abierta
    useEffect(() => {
        if (isCajero && !cajaStatusLoading && !cajaSession?.isOpen && availableCajas.length > 0 && !hasAutoShownOpenCajaRef.current) {
            hasAutoShownOpenCajaRef.current = true;
            setShowOpenCajaModal(true);
        }
        if (cajaSession?.isOpen) {
            hasAutoShownOpenCajaRef.current = false;
        }
    }, [isCajero, cajaStatusLoading, cajaSession?.isOpen, availableCajas.length]);

    // Este useEffect ya no es necesario porque el render condicional maneja la pantalla completa
    // El render condicional `if (cajeroMustOpenCaja)` muestra la pantalla completa cuando el cajero no tiene caja abierta
    // Solo mantener el useEffect para casos especiales (como cuando se viene de otra página con parámetro openCaja=1)

    useEffect(() => {
        if (cart.length === 0) {
            setBoletaCargoEnvase(false);
            setBoletaDescuentoDevolucionEnvase(false);
        }
    }, [cart.length]);

    useEffect(() => {
        if (!cajaSession?.isOpen) {
            setBoletaCargoEnvase(false);
            setBoletaDescuentoDevolucionEnvase(false);
        }
    }, [cajaSession?.isOpen]);

    // Debounced search - same as ProductsPage
    // Also detects preventa barcode (PRV), vendedor barcode (VND), and products
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            const trimmed = searchTerm.trim();
            if (!trimmed) {
                setProducts([]);
                return;
            }
            const searchUpper = searchTerm.toUpperCase().trim();
            // Check if scanned a preventa barcode (PRV00000123 format)
            if (searchUpper.startsWith('PRV') && searchUpper.length >= 4) {
                const preventaId = parseInt(searchUpper.substring(3), 10);
                if (!isNaN(preventaId) && preventaId > 0) {
                    if (isPreventaPrevendedor) {
                        showToast('Su rol solo genera preventas. El código PRV lo escanea caja para cobrar.', 'error');
                        setSearchTerm('');
                        setProducts([]);
                        setTimeout(() => searchInputRef.current?.focus(), 50);
                        return;
                    }
                    console.log('Preventa barcode detected:', preventaId);
                    loadPreventa(preventaId);
                    setSearchTerm('');
                    return;
                }
            }
            if (searchUpper.startsWith('VND') && searchUpper.length >= 4 && !canScanVendedorBarcode) {
                showToast('Los códigos de vendedor (VND) no están habilitados para su perfil.', 'error');
                setSearchTerm('');
                setProducts([]);
                setTimeout(() => searchInputRef.current?.focus(), 50);
                return;
            }
            if (canScanVendedorBarcode && searchUpper.startsWith('VND') && searchUpper.length >= 4) {
                const code = searchTerm.trim();
                fetch(`${API_URL}/api/users/vendedor-by-barcode/${encodeURIComponent(code)}`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                })
                    .then(res => {
                        if (res.ok) return res.json();
                        throw new Error(res.status === 404 ? 'Vendedor no encontrado' : 'Código no válido');
                    })
                    .then(data => {
                        setSelectedVendedor({ id: data.id, name: data.name });
                        showToast(`Vendedor asignado: ${data.name}`, 'success');
                        setSearchTerm('');
                        setProducts([]);
                        setTimeout(() => searchInputRef.current?.focus(), 50);
                    })
                    .catch(() => {
                        showToast('Código de vendedor no válido', 'error');
                        setSearchTerm('');
                        setTimeout(() => searchInputRef.current?.focus(), 50);
                    });
                return;
            }
            // Ticket envase — ENV + dígitos (trajo envase de vuelta)
            const envCodigoUpper = searchUpper.replace(/\s+/g, '');
            if (/^ENV\d{10,}$/.test(envCodigoUpper)) {
                if (!isCajero || !cajeroPuedeEjecutarCobro) {
                    showToast(
                        !isCajero
                            ? 'Solo personal de caja puede procesar la devolución de envase (escanee ENV…).'
                            : (cajaSession?.sales_blocked_reason ||
                              'Su turno no puede registrar ENV: abra una caja operativa con sucursal activa.'),
                        'error'
                    );
                    setSearchTerm('');
                    setProducts([]);
                    setTimeout(() => searchInputRef.current?.focus(), 50);
                    return;
                }
                setLoading(true);
                fetch(`${API_URL}/api/cajas/envase-devolver`, {
                    method: 'POST',
                    headers: { ...authHeader(), 'Content-Type': 'application/json' },
                    body: JSON.stringify({ codigo: envCodigoUpper }),
                })
                    .then(async res => {
                        const data = await res.json().catch(() => ({}));
                        if (res.status === 401) {
                            logout();
                            throw new Error('session');
                        }
                        if (!res.ok) throw new Error(data.error || 'No se pudo registrar la devolución');
                        return data;
                    })
                    .then(data => {
                        showToast(`Devolución envase ${formatCurrency(data.monto)} registrada. Entregue efectivo al cliente.`, 'success');
                        setSearchTerm('');
                        setProducts([]);
                        setTimeout(() => searchInputRef.current?.focus(), 50);
                    })
                    .catch(err => {
                        if (err.message !== 'session') {
                            showToast(err.message || 'Error al procesar código ENV', 'error');
                        }
                        setSearchTerm('');
                        setProducts([]);
                        setTimeout(() => searchInputRef.current?.focus(), 50);
                    })
                    .finally(() => setLoading(false));
                return;
            }
            // Enable auto-add on exact barcode match for scanner workflow
            fetchProducts(searchTerm, true);
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, canScanVendedorBarcode, isPreventaPrevendedor, isCajero, cajeroPuedeEjecutarCobro]);

    // Validate vendedor barcode when typing in preventa modal
    useEffect(() => {
        if (!showVendedorPreventaModal || !vendedorPreventaInput.trim()) {
            setVendedorPreventaResolved(null);
            return;
        }
        const upper = vendedorPreventaInput.toUpperCase().trim();
        if (!upper.startsWith('VND') || upper.length < 4) return;
        const timer = setTimeout(() => {
            fetch(`${API_URL}/api/users/vendedor-by-barcode/${encodeURIComponent(vendedorPreventaInput.trim())}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            })
                .then(res => res.ok ? res.json() : Promise.reject())
                .then(data => setVendedorPreventaResolved({ id: data.id, name: data.name }))
                .catch(() => setVendedorPreventaResolved(null));
        }, 400);
        return () => clearTimeout(timer);
    }, [showVendedorPreventaModal, vendedorPreventaInput]);

    // Helper to show toast notification
    const showToast = (message, type = 'error') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'error' }), 3000);
    };

    useIdleTimeout({
        enabled: requiereCajaOperativa && Boolean(user),
        timeoutMs: IDLE_TIMEOUT_MS,
        warnMs: IDLE_WARN_MS,
        onWarn: () => showToast('Su sesión expirará por inactividad en 1 minuto.', 'error'),
        onTimeout: () => {
            logout();
            navigate('/login');
        },
    });

    // Check if string looks like a barcode (numeric, 6-13 digits)
    const looksLikeBarcode = (str) => /^\d{6,14}$/.test(str.trim());

    // Data Fetching
    const fetchProducts = async (search = '', autoAddOnExactMatch = false) => {
        setLoading(true);
        try {
            const sucursalId = cajaSession?.session?.sucursal_id || availableCajas[0]?.sucursal_id || 1;
            const params = new URLSearchParams();
            if (search) params.set('search', search);
            params.set('sucursal_id', sucursalId);
            params.set('pos_search', '1'); // Código exacto; nombre solo productos sin codigo_barra
            params.set('limit', '800');
            const url = `${API_URL}/api/products?${params.toString()}`;
            console.log('Fetching products from:', url);
            const res = await fetch(url, { headers: authHeader() });
            if (res.ok) {
                const data = await res.json();
                console.log('Products received:', data.length);
                // Sort: products with stock first, products without stock at the end
                const sorted = [...data].sort((a, b) => {
                    const sa = a.stock_actual ?? 0;
                    const sb = b.stock_actual ?? 0;
                    if (sa <= 0 && sb > 0) return 1;
                    if (sa > 0 && sb <= 0) return -1;
                    return (a.nombre || '').localeCompare(b.nombre || '');
                });
                
                const searchTrimmed = search.trim();
                const isBarcodeScan = looksLikeBarcode(searchTrimmed);
                
                // Check for exact barcode match (scanner detection)
                if (autoAddOnExactMatch && searchTrimmed) {
                    const exactMatch = data.find(p => 
                        p.codigo_barra && p.codigo_barra.toLowerCase() === searchTrimmed.toLowerCase()
                    );
                    
                    if (exactMatch) {
                        console.log('Exact barcode match found, auto-adding:', exactMatch.nombre);
                        if (exactMatch.venta_por_peso) {
                            setPesoProductToAdd(exactMatch);
                            setPesoGramsInput('');
                            setShowPesoModal(true);
                        } else {
                            addToCart(exactMatch, true);
                        }
                        setSearchTerm('');
                        setProducts([]);
                        setTimeout(() => searchInputRef.current?.focus(), 50);
                        return;
                    }
                    
                    // If it looks like a barcode scan but no exact match found
                    if (isBarcodeScan) {
                        console.log('Barcode not found:', searchTrimmed);
                        showToast(`Producto no encontrado: ${searchTrimmed}`, 'error');
                        setSearchTerm('');
                        setProducts([]);
                        setTimeout(() => searchInputRef.current?.focus(), 50);
                        return;
                    }
                }
                
                setProducts(sorted);
            } else {
                console.error('API error:', res.status, res.statusText);
                setProducts([]);
            }
        } catch (err) { 
            console.error('Fetch error:', err); 
            setProducts([]);
        }
        finally { setLoading(false); }
    };

    const fetchAvailableCajas = async () => {
        try {
            const url = isCajero
                ? `${API_URL}/api/cajas/available-for-me`
                : `${API_URL}/api/cajas`;
            const res = await fetch(url, { headers: authHeader() });
            if (res.status === 401) { logout(); return; }
            if (res.ok) {
                const data = await res.json();
                setAvailableCajas(Array.isArray(data) ? data : []);
            } else {
                const errBody = await res.json().catch(() => ({}));
                const msg = errBody.error || `No se pudieron cargar las cajas (código ${res.status}).`;
                console.error('Cajas:', res.status, errBody);
                setAvailableCajas([]);
                if (isCajero) showToast(msg, 'error');
            }
        } catch (err) {
            console.error(err);
            setAvailableCajas([]);
            if (isCajero) showToast('Error de red al cargar las cajas.', 'error');
        }
    };

    const fetchPromotions = async () => {
        try {
            const res = await fetch(`${API_URL}/api/promotions`, { headers: authHeader() });
            if (res.status === 401) { logout(); return; }
            if (res.ok) setPromotions(await res.json());
        } catch (err) { console.error(err); }
    };

    useEffect(() => {
        if (searchTerm.trim() && promotions.length > 0) {
            const term = searchTerm.toLowerCase().trim();
            const filtered = promotions.filter(p =>
                p.nombre?.toLowerCase().includes(term) ||
                p.descripcion?.toLowerCase().includes(term)
            );
            setPromotionsInSearch(filtered);
        } else {
            setPromotionsInSearch([]);
        }
    }, [searchTerm, promotions]);

    const fetchCajaStatus = async () => {
        setCajaStatusLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/cajas/status`, {
                headers: authHeader()
            });
            if (res.status === 401) { logout(); return; }
            if (res.ok) {
                const data = await res.json();
                console.log('✅ Caja status fetched:', data);
                console.log('📊 Estado anterior:', cajaSession);
                setCajaSession(data);
                console.log('📊 Estado nuevo será:', data);
                // Si la caja está abierta, asegurarse de que el modal esté cerrado
                if (data.isOpen) {
                    console.log('🔓 Caja abierta, cerrando modal');
                    setShowOpenCajaModal(false);
                } else {
                    console.log('🔒 Caja cerrada');
                }
            } else {
                console.error('❌ Error en respuesta de caja status:', res.status, res.statusText);
                setCajaSession({ isOpen: false });
            }
        } catch (err) { 
            console.error('❌ Error fetching caja status:', err);
            setCajaSession({ isOpen: false });
        } finally {
            setCajaStatusLoading(false);
        }
    };

    const fetchPreventas = async () => {
        try {
            const res = await fetch(`${API_URL}/api/preventas`, {
                headers: authHeader()
            });
            if (res.status === 401) { logout(); return; }
            if (res.ok) setPreventas(await res.json());
        } catch (err) { console.error(err); }
    };

    // Actions
    const handleOpenCaja = async (e) => {
        e.preventDefault();
        const montoInicial = parseMontoClp(openCajaData.monto_inicial) ?? 0;
        if (!openCajaData.caja_id || montoInicial < 0) {
            showToast('Debe seleccionar una caja e ingresar el monto en efectivo.', 'error');
            return;
        }
        const mismatch = validateDesgloseVsMonto(openCajaDesglose, montoInicial);
        if (mismatch) {
            showToast(mismatch, 'error');
            return;
        }
        setCajaStatusLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/cajas/open`, {
                method: 'POST',
                headers: { ...authHeader(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    caja_id: openCajaData.caja_id,
                    monto_inicial: montoInicial,
                    desglose: compactDesglose(openCajaDesglose),
                })
            });
            if (res.status === 401) { logout(); return; }
            if (res.ok) {
                const result = await res.json();
                console.log('✅ Caja abierta exitosamente:', result);
                
                setOpenCajaData({ caja_id: '', monto_inicial: '' });
                setOpenCajaDesglose(createEmptyDesglose());
                setShowOpenCajaModal(false);
                
                const newCajaSession = { 
                    isOpen: true, 
                    session: result 
                };
                setCajaSession(newCajaSession);
                setCajaStatusLoading(false);
                
                showToast('Caja abierta correctamente. Ya puede registrar ventas.', 'success');
                
                setTimeout(async () => {
                    const statusRes = await fetch(`${API_URL}/api/cajas/status`, {
                        headers: authHeader()
                    });
                    if (statusRes.ok) {
                        const statusData = await statusRes.json();
                        if (statusData.isOpen) {
                            setCajaSession(statusData);
                        }
                    }
                }, 300);
            } else {
                const errorData = await res.json().catch(() => ({}));
                console.error('❌ Error al abrir caja:', errorData);
                const errMsg = errorData.error || 'Error al abrir la caja';
                if (errMsg.includes('already has an open caja')) {
                    setShowOpenCajaModal(false);
                    fetchCajaStatus();
                    showToast('Ya tiene una caja abierta. Puede continuar operando.', 'success');
                } else {
                    alert(errMsg);
                }
                setCajaStatusLoading(false);
            }
        } catch (err) {
            console.error('❌ Error al abrir caja:', err);
            alert('Error al abrir la caja');
            setCajaStatusLoading(false);
        }
    };

    const handleCloseCaja = async (e) => {
        e.preventDefault();
        const efectivo = parseMontoClp(closeCajaData.efectivo_cierre);
        if (efectivo === null) {
            showToast('Debe ingresar el monto de efectivo (conteo físico en caja).', 'error');
            return;
        }
        const mismatch = validateDesgloseVsMonto(closeCajaDesglose, efectivo);
        if (mismatch) {
            showToast(mismatch, 'error');
            return;
        }

        const esperado = cierreResumen?.efectivo_esperado_en_caja ?? null;
        const diff =
            esperado != null && Number.isFinite(Number(esperado))
                ? efectivo - Number(esperado)
                : null;
        const hayDescuadre = diff != null && Math.abs(diff) > 1;

        if (hayDescuadre && !closeCajaForzar) {
            showToast(
                `El conteo difiere del sistema (${formatCurrency(diff)}). Marque "Cerrar con descuadre" o ajuste el monto.`,
                'error'
            );
            return;
        }

        if (closeCajaForzar && hayDescuadre) {
            const ok = window.confirm(
                `El efectivo contado (${formatCurrency(efectivo)}) no coincide con el esperado (${formatCurrency(esperado)}).\n\n` +
                    `Diferencia: ${formatCurrency(diff)}\n\n` +
                    '¿Cerrar el turno de todas formas? Quedará registrado en el corte.'
            );
            if (!ok) return;
        }

        const payload = {
            efectivo_cierre: efectivo,
            desglose: compactDesglose(closeCajaDesglose),
            ...(closeCajaForzar && hayDescuadre ? { forzar_cierre: true } : {}),
        };
        try {
            const res = await fetch(`${API_URL}/api/cajas/close`, {
                method: 'POST',
                headers: { ...authHeader(), 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (res.status === 401) { logout(); navigate('/login'); return; }
            if (res.ok) {
                const data = await res.json().catch(() => ({}));
                if (data.corte_turno) {
                    printCorteTurno(data.corte_turno);
                }
                setShowCloseCajaModal(false);
                setCierreResumen(null);
                setCloseCajaData({ efectivo_cierre: '' });
                setCloseCajaDesglose(createEmptyDesglose());
                setCloseCajaForzar(false);
                logout();
                navigate('/login');
            } else {
                const errorData = await res.json().catch(() => ({}));
                const msg = errorData.detalle?.efectivo
                    ? `${errorData.error}\nEsperado: ${formatCurrency(errorData.detalle.efectivo.esperado)}\nContado: ${formatCurrency(errorData.detalle.efectivo.ingresado)}\nDiferencia: ${formatCurrency(errorData.detalle.efectivo.diff ?? 0)}${errorData.puede_forzar_cierre ? '\n\nPuede marcar "Cerrar con descuadre" para continuar.' : ''}`
                    : (errorData.error || 'No se pudo cerrar la caja');
                alert(msg);
            }
        } catch (err) {
            console.error(err);
            alert('No se pudo cerrar la caja. Por favor intente nuevamente.');
        }
    };

    const openCloseCajaModal = async () => {
        setCloseCajaForzar(false);
        setCloseCajaDesglose(createEmptyDesglose());
        try {
            const res = await fetch(`${API_URL}/api/cajas/cierre-resumen`, { headers: authHeader() });
            if (res.status === 401) { logout(); return; }
            if (res.ok) {
                const data = await res.json();
                setCierreResumen(data);
                const esperado = Number(data.efectivo_esperado_en_caja);
                const prefill =
                    Number.isFinite(esperado) && esperado >= 0 ? String(Math.round(esperado)) : '';
                setCloseCajaData({ efectivo_cierre: prefill });
            } else {
                setCierreResumen(null);
                setCloseCajaData({ efectivo_cierre: '' });
            }
        } catch (err) {
            console.error(err);
            setCierreResumen(null);
            setCloseCajaData({ efectivo_cierre: '' });
        }
        setShowCloseCajaModal(true);
    };

    /** Register utility/account payment (cash in drawer, or external card — audit only). */
    const handlePagoCuenta = async () => {
        const monto = Math.round(Number(String(pagoCuentaMonto).replace(/\./g, '')) || 0);
        if (monto <= 0) {
            showToast('Indique un monto válido para el pago de cuenta.', 'error');
            return;
        }
        const tipo = pagoCuentaModo === 'tarjeta_externa' ? 'pago_cuenta_tarjeta_externa' : 'pago_cuenta_efectivo';
        setPagoCuentaLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/cajas/ajuste-efectivo`, {
                method: 'POST',
                headers: { ...authHeader(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ tipo, monto }),
            });
            if (res.status === 401) {
                logout();
                return;
            }
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                showToast(data.error || 'No se pudo registrar el pago de cuenta.', 'error');
                return;
            }
            const etiqueta = pagoCuentaModo === 'tarjeta_externa' ? 'Pago de cuenta (tarjeta externa)' : 'Pago de cuenta (efectivo)';
            showToast(`${etiqueta} ${formatCurrency(monto)} registrado.`, 'success');
            setShowPagoCuentaModal(false);
            setPagoCuentaMonto('');
            setPagoCuentaModo('efectivo');
            refreshEfectivoCaja();
        } catch (err) {
            console.error(err);
            showToast('Error de red al registrar pago de cuenta.', 'error');
        } finally {
            setPagoCuentaLoading(false);
        }
    };

    /** Cash injection or security withdrawal during open shift. */
    const handleMovimientoEfectivo = async () => {
        const monto = parseMontoClp(movimientoEfectivoMonto) ?? 0;
        if (monto <= 0) {
            showToast('Indique un monto válido.', 'error');
            return;
        }
        const mismatch = validateDesgloseVsMonto(movimientoEfectivoDesglose, monto);
        if (mismatch) {
            showToast(mismatch, 'error');
            return;
        }
        setMovimientoEfectivoLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/cajas/ajuste-efectivo`, {
                method: 'POST',
                headers: { ...authHeader(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    tipo: movimientoEfectivoTipo,
                    monto,
                    desglose: compactDesglose(movimientoEfectivoDesglose),
                }),
            });
            if (res.status === 401) {
                logout();
                return;
            }
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                showToast(data.error || 'No se pudo registrar el movimiento.', 'error');
                return;
            }
            const etiqueta =
                movimientoEfectivoTipo === 'inyeccion_efectivo'
                    ? 'Inyección de efectivo'
                    : 'Retiro por seguridad';
            showToast(`${etiqueta} ${formatCurrency(monto)} registrado.`, 'success');
            setShowMovimientoEfectivoModal(false);
            setMovimientoEfectivoMonto('');
            setMovimientoEfectivoDesglose(createEmptyDesglose());
            setMovimientoEfectivoSugerido(null);
            refreshEfectivoCaja();
        } catch (err) {
            console.error(err);
            showToast('Error de red al registrar movimiento de efectivo.', 'error');
        } finally {
            setMovimientoEfectivoLoading(false);
        }
    };

    const doCreatePreventa = async (vendedorToUse) => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/preventas`, {
                method: 'POST',
                headers: { ...authHeader(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    items: cart,
                    total: calculateTotal(),
                    vendedor_id: vendedorToUse?.id || undefined
                })
            });

            if (res.status === 401) { logout(); return; }

            if (res.ok) {
                const data = await res.json();

                // Print Ticket (vendedor identificado por código VND)
                printTicket({
                    id: data.preventaId,
                    vendedor: vendedorToUse?.name || user?.name || 'Vendedor',
                    items: cart,
                    total: calculateTotal(),
                    date: new Date()
                });

                alert(`Preventa #${data.preventaId} generada correctamente. Imprimiendo ticket...`);
                setCart([]);
                setShowVendedorPreventaModal(false);
                setVendedorPreventaInput('');
                setVendedorPreventaResolved(null);
            } else {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || 'No se pudo crear la preventa');
            }
        } catch (err) {
            alert(err.message || 'No se pudo crear la preventa. Por favor intente nuevamente.');
        } finally {
            setLoading(false);
        }
    };

    const printTicket = (ticketData) => {
        const printWindow = window.open('', '_blank', 'width=350,height=700');
        if (!printWindow) {
            alert('Por favor habilite las ventanas emergentes para imprimir el ticket.');
            return;
        }

        // Generate barcode code: PRV + ID padded to 8 digits
        const barcodeValue = `PRV${String(ticketData.id).padStart(8, '0')}`;

        const html = `
            <!DOCTYPE html>
            <html>
                <head>
                    <title>Ticket Preventa #${ticketData.id}</title>
                    <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"></script>
                    <style>
                        @page { margin: 0; }
                        body { 
                            font-family: 'Arial Black', 'Arial', sans-serif; 
                            width: 76mm; 
                            margin: 3mm; 
                            font-size: 14px; 
                            line-height: 1.4;
                            font-weight: bold;
                        }
                        .header { text-align: center; margin-bottom: 12px; }
                        .store-name { font-size: 22px; font-weight: 900; letter-spacing: 1px; }
                        .store-info { font-size: 12px; font-weight: normal; }
                        .title { 
                            font-size: 20px; 
                            font-weight: 900; 
                            margin: 8px 0; 
                            padding: 8px 0;
                            border-top: 2px solid #000; 
                            border-bottom: 2px solid #000; 
                            text-align: center;
                            letter-spacing: 2px;
                        }
                        .info { font-size: 13px; margin-bottom: 3px; text-align: left; font-weight: 600; }
                        .table { width: 100%; margin-top: 12px; border-collapse: collapse; }
                        .table th { 
                            text-align: left; 
                            border-bottom: 2px solid #000; 
                            font-size: 13px; 
                            font-weight: 900;
                            padding: 4px 0;
                        }
                        .table td { 
                            text-align: left; 
                            padding: 4px 0; 
                            vertical-align: top; 
                            font-size: 13px;
                            font-weight: 600;
                        }
                        .qty { width: 12%; }
                        .desc { width: 55%; }
                        .price { width: 33%; text-align: right; }
                        .total-section { 
                            margin-top: 12px; 
                            border-top: 2px solid #000; 
                            padding-top: 8px; 
                            text-align: right; 
                        }
                        .total-line { 
                            font-weight: 900; 
                            font-size: 20px; 
                            letter-spacing: 1px;
                        }
                        .footer { 
                            margin-top: 15px; 
                            text-align: center; 
                            font-size: 14px; 
                            font-weight: 900;
                            padding: 8px;
                            background: #f0f0f0;
                            border-radius: 4px;
                        }
                        .barcode-section { 
                            margin-top: 15px; 
                            text-align: center; 
                            padding: 15px 5px;
                            border: 2px solid #000;
                            background: #fff;
                        }
                        .barcode-label {
                            font-size: 14px;
                            font-weight: 900;
                            color: #000;
                            margin-bottom: 8px;
                            text-transform: uppercase;
                            letter-spacing: 1px;
                        }
                        #barcode {
                            max-width: 100%;
                        }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <div class="store-name">ERP MINIMARKET</div>
                        <div class="store-info">Sucursal Central</div>
                    </div>
                    
                    <div class="info">Fecha: ${ticketData.date.toLocaleString('es-CL')}</div>
                    <div class="info">Atendido por: ${ticketData.vendedor}</div>
                    
                    <div class="title">PREVENTA #${ticketData.id}</div>

                    <table class="table">
                        <thead>
                            <tr>
                                <th class="qty">Cant</th>
                                <th class="desc">Producto</th>
                                <th class="price">Subtotal</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${ticketData.items.map(item => `
                                <tr>
                                    <td class="qty">${item.quantity}</td>
                                    <td class="desc">${item.nombre.substring(0, 22)}</td>
                                    <td class="price">${formatCurrency(item.price * item.quantity)}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>

                    <div class="total-section">
                        <div class="total-line">TOTAL: ${formatCurrency(ticketData.total)}</div>
                    </div>

                    <div class="footer">
                        ★ PASE A CAJA CON ESTE TICKET ★
                    </div>

                    <div class="barcode-section">
                        <div class="barcode-label">PRESENTE EN CAJA</div>
                        <svg id="barcode"></svg>
                    </div>

                    <div style="text-align: center; margin-top: 10px; font-size: 11px; font-weight: normal;">
                        Código: ${barcodeValue}<br/>
                        Validez: 24 horas
                    </div>
                    
                    <script>
                        window.onload = function() {
                            // Generate barcode
                            JsBarcode("#barcode", "${barcodeValue}", {
                                format: "CODE128",
                                width: 2,
                                height: 50,
                                displayValue: false,
                                margin: 5
                            });
                            // Print after barcode renders
                            setTimeout(function() {
                                window.print();
                                setTimeout(function() { window.close(); }, 500);
                            }, 300);
                        }
                    </script>
                </body>
            </html>
        `;

        printWindow.document.write(html);
        printWindow.document.close();
    };

    /** Ticket físico orientativo para preventa (sin registro en caja). */
    const printEnvasesTicket = () => {
        const printWindow = window.open('', '_blank', 'width=350,height=700');
        if (!printWindow) {
            alert('Por favor habilite las ventanas emergentes para imprimir.');
            return;
        }
        const noteHtml =
            'Documento orientativo. Para devolución con código ENV, el cliente presenta el ticket en caja.';

        const html = `<!DOCTYPE html><html><head><title>Ticket envases</title>
            <style>
            @page { margin: 0; }
            body { font-family: Arial Black, Arial, sans-serif; width: 76mm; margin: 3mm; font-size: 14px; line-height: 1.45; font-weight: bold; }
            .header { text-align: center; margin-bottom: 12px; }
            .store-name { font-size: 22px; font-weight: 900; letter-spacing: 1px; }
            .title { font-size: 18px; font-weight: 900; margin: 12px 0; padding: 10px 0; border-top: 3px solid #000; border-bottom: 3px solid #000; text-align: center; }
            .info { font-size: 13px; font-weight: 600; margin: 8px 0; }
            .amount { font-size: 28px; text-align: center; margin: 16px 0; font-weight: 900; }
            .note { font-size: 11px; font-weight: 600; margin-top: 14px; border-top: 1px dashed #000; padding-top: 10px; }
            .footer { margin-top: 16px; text-align: center; font-size: 12px; font-weight: 600; }
            </style></head><body>
            <div class="header"><div class="store-name">ERP MINIMARKET</div></div>
            <div class="title">ENVASE RETORNABLE</div>
            <div class="info">Fecha: ${new Date().toLocaleString('es-CL')}</div>
            <div class="info">Atendido por: ${(user?.name || '').replace(/</g, '')}</div>
            <p class="amount">${formatCurrency(ENVASE_TICKET_MONTO_CLP)}</p>
            <div class="note">${noteHtml}</div>
            <div class="footer">Documento orientativo — conserve si procede</div>
            <script>window.onload=function(){window.print();setTimeout(function(){window.close();},500);}</script>
            </body></html>`;
        printWindow.document.write(html);
        printWindow.document.close();
    };

    // Print Sale Receipt
    const printSaleReceipt = (saleData) => {
        const printWindow = window.open('', '_blank', 'width=350,height=700');
        if (!printWindow) {
            alert('Por favor habilite las ventanas emergentes para imprimir.');
            return;
        }

        const preventaInfo = saleData.preventaId ? '<div class="info">Preventa: #' + saleData.preventaId + '</div>' : '';
        
        // Build payment methods HTML
        let paymentMethodsHtml = '';
        if (saleData.payments && saleData.payments.length > 0) {
            paymentMethodsHtml = saleData.payments.map(p => 
                '<div><strong>' + p.name + ':</strong> ' + formatCurrency(p.amount) + '</div>'
            ).join('');
            const vueltoEfec = saleData.vuelto_efectivo || 0;
            if (saleData.change > 0 && !vueltoEfec) {
                paymentMethodsHtml += '<div style="margin-top:5px;border-top:1px dashed #ccc;padding-top:5px;"><strong>Vuelto:</strong> ' + formatCurrency(saleData.change) + '</div>';
            }
            if (vueltoEfec > 0) {
                paymentMethodsHtml += '<div style="margin-top:5px;border-top:1px dashed #ccc;padding-top:5px;"><strong>Vuelto en efectivo (desde caja):</strong> ' + formatCurrency(vueltoEfec) + '</div>';
            }
        }
        
        const itemsHtml = saleData.items
            .map((item) => {
                const line = item.venta_por_peso ? item.price || 0 : item.price * item.quantity;
                return (
                    '<tr><td class="qty">' +
                    item.quantity +
                    '</td><td class="desc">' +
                    item.nombre.substring(0, 22) +
                    '</td><td class="price">' +
                    formatCurrency(line) +
                    '</td></tr>'
                );
            })
            .join('');

        let envaseRowsHtml = '';
        if (saleData.boletaCargoEnvase) {
            envaseRowsHtml +=
                '<tr><td class="qty">1</td><td class="desc">Cargo envase retornable</td><td class="price">' +
                formatCurrency(ENVASE_TICKET_MONTO_CLP) +
                '</td></tr>';
        }
        if (saleData.boletaDescuentoDevolucionEnvase) {
            envaseRowsHtml +=
                '<tr><td class="qty">1</td><td class="desc">Devolución envase</td><td class="price">' +
                formatCurrency(-ENVASE_TICKET_MONTO_CLP) +
                '</td></tr>';
        }

        const html = '<!DOCTYPE html><html><head><title>Boleta Venta #' + saleData.id + '</title>' +
            '<style>' +
            '@page { margin: 0; }' +
            'body { font-family: Arial Black, Arial, sans-serif; width: 76mm; margin: 3mm; font-size: 14px; line-height: 1.4; font-weight: bold; }' +
            '.header { text-align: center; margin-bottom: 12px; }' +
            '.store-name { font-size: 22px; font-weight: 900; letter-spacing: 1px; }' +
            '.store-info { font-size: 12px; font-weight: normal; }' +
            '.title { font-size: 20px; font-weight: 900; margin: 8px 0; padding: 10px 0; border-top: 3px solid #000; border-bottom: 3px solid #000; text-align: center; letter-spacing: 2px; }' +
            '.info { font-size: 13px; margin-bottom: 3px; text-align: left; font-weight: 600; }' +
            '.table { width: 100%; margin-top: 12px; border-collapse: collapse; }' +
            '.table th { text-align: left; border-bottom: 2px solid #000; font-size: 13px; font-weight: 900; padding: 4px 0; }' +
            '.table td { text-align: left; padding: 4px 0; vertical-align: top; font-size: 13px; font-weight: 600; }' +
            '.qty { width: 12%; } .desc { width: 55%; } .price { width: 33%; text-align: right; }' +
            '.total-section { margin-top: 12px; border-top: 2px solid #000; padding-top: 8px; }' +
            '.total-line { font-weight: 900; font-size: 22px; text-align: right; }' +
            '.payment-info { margin-top: 10px; padding: 8px; background: #f5f5f5; border-radius: 4px; font-size: 13px; }' +
            '.footer { margin-top: 20px; text-align: center; font-size: 13px; font-weight: 600; }' +
            '</style></head><body>' +
            '<div class="header"><div class="store-name">ERP MINIMARKET</div><div class="store-info">Sucursal Central</div></div>' +
            '<div class="title">BOLETA DE VENTA</div>' +
            '<div class="info">Venta #' + saleData.id + '</div>' +
            '<div class="info">Fecha: ' + saleData.date.toLocaleString('es-CL') + '</div>' +
            '<div class="info">Cajero: ' + saleData.cajero + '</div>' +
            preventaInfo +
            '<table class="table"><thead><tr><th class="qty">Cant</th><th class="desc">Producto</th><th class="price">Subtotal</th></tr></thead><tbody>' +
            itemsHtml +
            envaseRowsHtml +
            '</tbody></table>' +
            '<div class="total-section"><div class="total-line">TOTAL: ' + formatCurrency(saleData.total) + '</div></div>' +
            '<div class="payment-info"><strong>Forma de pago:</strong><br/>' + paymentMethodsHtml + '</div>' +
            '<div class="footer">¡Gracias por su compra!<br/><span style="font-size: 11px; font-weight: normal;">Conserve este comprobante</span></div>' +
            '<script>window.onload = function() { window.print(); setTimeout(function() { window.close(); }, 500); }</script>' +
            '</body></html>';

        printWindow.document.write(html);
        printWindow.document.close();
    };

    const handleCompleteSale = () => {
        if (cart.length === 0) return;
        if (saleRequiresVnd && !selectedVendedor) {
            showToast('Debe escanear el código de vendedor (VND) antes de completar la venta.', 'error');
            return;
        }
        setPaymentData({ efectivo: 0, tarjeta_debito: 0, tarjeta_credito: 0 });
        setVueltoEfectivo(0);
        if (requiereCajaOperativa && cajaSession?.isOpen) {
            refreshEfectivoCaja();
        }
        setShowPaymentModal(true);
    };

    // Calculate total paid from all payment methods
    const getTotalPaid = () => {
        return (parseFloat(paymentData.efectivo) || 0) + 
               (parseFloat(paymentData.tarjeta_debito) || 0) + 
               (parseFloat(paymentData.tarjeta_credito) || 0);
    };

    /** Same vuelto split as backend saleController — all change is physical cash out. */
    const computeVueltoDrawerImpact = (saleTotal, payments, vueltoManualRaw) => {
        const efectivoPagado = Math.round(Number(payments?.efectivo) || 0);
        const totalPaid =
            efectivoPagado +
            Math.round(Number(payments?.tarjeta_debito) || 0) +
            Math.round(Number(payments?.tarjeta_credito) || 0);
        const vueltoTotal = Math.max(0, Math.round(totalPaid - saleTotal));
        const efectivoExcedente = Math.max(0, efectivoPagado - saleTotal);
        let vueltoEfectivo = Math.min(vueltoTotal, efectivoExcedente);
        const vueltoManual = Math.max(0, Math.round(Number(vueltoManualRaw) || 0));
        if (vueltoManual > 0 && vueltoManual <= vueltoTotal + 1) {
            vueltoEfectivo = Math.min(vueltoManual, efectivoExcedente, vueltoTotal);
        }
        return { totalPaid, vueltoTotal, vueltoEfectivo, efectivoPagado };
    };

    /** Cash shortfall to deliver change: disponible + efectivo recibido en venta >= vuelto. */
    const computeEfectivoFaltanteParaVuelto = (efectivoDisponible, saleTotal, payments, vueltoManualRaw) => {
        if (efectivoDisponible == null || !Number.isFinite(Number(efectivoDisponible))) return null;
        const { vueltoTotal, efectivoPagado } = computeVueltoDrawerImpact(
            saleTotal,
            payments,
            vueltoManualRaw
        );
        if (vueltoTotal <= 0) {
            return { falta: 0, vueltoTotal: 0, efectivoDisponible: Number(efectivoDisponible), efectivoPagado };
        }
        const falta = Math.max(0, vueltoTotal - Number(efectivoDisponible) - efectivoPagado);
        return {
            falta,
            vueltoTotal,
            efectivoDisponible: Number(efectivoDisponible),
            efectivoPagado,
        };
    };

    const refreshEfectivoCaja = async () => {
        try {
            const res = await fetch(`${API_URL}/api/cajas/status`, { headers: authHeader() });
            if (res.status === 401) {
                logout();
                return;
            }
            if (res.ok) {
                const data = await res.json();
                setCajaSession((prev) => (prev?.isOpen ? { ...prev, ...data } : data));
            }
        } catch (err) {
            console.error(err);
        }
    };

    const abrirInyeccionDesdePago = (montoSugerido) => {
        setMovimientoEfectivoTipo('inyeccion_efectivo');
        setMovimientoEfectivoDesglose(createEmptyDesglose());
        const sugerido = montoSugerido > 0 ? Math.round(montoSugerido) : null;
        setMovimientoEfectivoSugerido(sugerido);
        setMovimientoEfectivoMonto(sugerido != null ? String(sugerido) : '');
        setShowMovimientoEfectivoModal(true);
    };

    // Get payment methods summary for receipt
    const getPaymentMethodsSummary = () => {
        const methods = [];
        if (paymentData.efectivo > 0) methods.push({ name: 'Efectivo', amount: paymentData.efectivo });
        if (paymentData.tarjeta_debito > 0) methods.push({ name: 'Tarjeta Débito', amount: paymentData.tarjeta_debito });
        if (paymentData.tarjeta_credito > 0) methods.push({ name: 'Tarjeta Crédito', amount: paymentData.tarjeta_credito });
        return methods;
    };

    const confirmSale = async () => {
        setLoading(true);
        if (requiereCajaOperativa && !puedeEjecutarCobro) {
            showToast(
                cajaSession?.sales_blocked_reason ||
                    'No puede cobrar: active la sucursal y marque la caja como operativa en Gestión de sucursales o en Cajas.',
                'error'
            );
            setLoading(false);
            return;
        }
        const totalPaid = getTotalPaid();
        const saleTotal = calculateTotal();
        const vueltoRegistrado =
            isCajero && cajeroPuedeEjecutarCobro ? Math.max(0, Math.round(Number(vueltoEfectivo) || 0)) : 0;
        const maxVueltoPermitido = Math.max(0, Math.round(totalPaid - saleTotal));
        if (vueltoRegistrado > maxVueltoPermitido + 1) {
            alert('El vuelto en efectivo no puede superar lo cobrado menos el total de la venta.');
            setLoading(false);
            return;
        }
        if (vueltoRegistrado > 0 && saleTotal < 1000) {
            alert('La venta debe ser de al menos $1.000 para registrar vuelto en efectivo desde caja.');
            setLoading(false);
            return;
        }

        const vueltoManualParaChequeo =
            isCajero && cajeroPuedeEjecutarCobro ? vueltoRegistrado : 0;
        const chequeoEfectivo = computeEfectivoFaltanteParaVuelto(
            cajaSession?.efectivo_disponible_en_caja,
            saleTotal,
            paymentData,
            vueltoManualParaChequeo
        );
        if (chequeoEfectivo && chequeoEfectivo.falta > 0) {
            alert(
                `No hay suficiente efectivo en caja para entregar el vuelto.\n\n` +
                    `Disponible en caja: ${formatCurrency(chequeoEfectivo.efectivoDisponible)}\n` +
                    `Efectivo recibido (esta venta): ${formatCurrency(chequeoEfectivo.efectivoPagado)}\n` +
                    `Vuelto a entregar: ${formatCurrency(chequeoEfectivo.vueltoTotal)}\n` +
                    `Faltan: ${formatCurrency(chequeoEfectivo.falta)}\n\n` +
                    `Use «Inyección de efectivo» (↓) antes de confirmar el pago.`
            );
            setLoading(false);
            return;
        }

        const change = vueltoRegistrado > 0 ? 0 : Math.max(0, totalPaid - saleTotal);
        const cargoBoleta = opcionesSesionCajaVentasActivas && boletaCargoEnvase;
        const descBoleta = opcionesSesionCajaVentasActivas && boletaDescuentoDevolucionEnvase;
        let res;
        try {
            res = await fetch(`${API_URL}/api/sales`, {
                method: 'POST',
                headers: { ...authHeader(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    items: cart,
                    total: saleTotal,
                    cajaId: cajaSession?.session?.caja_id || null,
                    preventa_id: activePreventaId,
                    vendedor_id: selectedVendedor?.id ?? null,
                    payments: paymentData,
                    totalPaid: totalPaid,
                    vuelto_efectivo: vueltoRegistrado,
                    envase: cargoBoleta
                        ? { tipo: 'cargo', monto: ENVASE_TICKET_MONTO_CLP }
                        : descBoleta
                            ? { tipo: 'devolucion', monto: ENVASE_TICKET_MONTO_CLP }
                            : null,
                })
            });

            if (res.status === 401) { logout(); return; }

            if (res.ok) {
                const saleResult = await res.json();
                
                // Print sale receipt with multiple payment methods
                printSaleReceipt({
                    id: saleResult.ventaId || saleResult.id || Date.now(),
                    date: new Date(),
                    cajero: user?.name || 'Cajero',
                    preventaId: activePreventaId,
                    items: cart,
                    total: saleTotal,
                    payments: getPaymentMethodsSummary(),
                    totalPaid: totalPaid,
                    change: change,
                    vuelto_efectivo: vueltoRegistrado,
                    boletaCargoEnvase: cargoBoleta,
                    boletaDescuentoDevolucionEnvase: descBoleta,
                });

                alert('Venta Completada Exitosamente!');
                setCart([]);
                setActivePreventaId(null);
                setSelectedVendedor(null);
                setBoletaCargoEnvase(false);
                setBoletaDescuentoDevolucionEnvase(false);
                setVueltoEfectivo(0);
                setShowPaymentModal(false);
                if (requiereCajaOperativa) {
                    refreshEfectivoCaja();
                }
            } else {
                throw new Error('Failed');
            }
        } catch (err) {
            let errMsg = 'Error al procesar la venta';
            try {
                if (res && !res.ok) {
                    const data = await res.json().catch(() => ({}));
                    errMsg = data.error || errMsg;
                    if (data.detalle?.falta_efectivo > 0) {
                        errMsg +=
                            `\n\nDisponible: ${formatCurrency(data.detalle.efectivo_disponible)}\n` +
                            `Vuelto: ${formatCurrency(data.detalle.vuelto_requerido)}\n` +
                            `Faltan: ${formatCurrency(data.detalle.falta_efectivo)}`;
                    }
                } else if (err?.message) {
                    errMsg = err.message;
                }
            } catch (_) {}
            alert(errMsg);
        } finally {
            setLoading(false);
        }
    };

    const loadPreventa = async (id) => {
        if (isPreventaPrevendedor) {
            showToast('Su rol solo genera preventas. Para cobrar una preventa use personal de caja.', 'error');
            return;
        }
        try {
            const res = await fetch(`${API_URL}/api/preventas/${id}`, {
                headers: authHeader()
            });
            if (res.status === 401) { logout(); return; }
            if (!res.ok) {
                showToast('Preventa no encontrada o no disponible', 'error');
                return;
            }

            const data = await res.json();

            if (data.estado === false) {
                showToast('Esta preventa ya fue cobrada y no puede cargarse.', 'error');
                return;
            }

            // Transform items to match cart structure (products, promos, weight sales)
            const cartItems = data.items.map(i => {
                if (i.promocion_id) {
                    const promoCartId = i.producto_id
                        ? `promo-${i.promocion_id}-${i.producto_id}`
                        : `promo-${i.promocion_id}`;
                    return {
                        id: promoCartId,
                        isPromo: true,
                        promocion_id: i.promocion_id,
                        nombre: i.promocion_nombre
                            ? `Promo: ${i.promocion_nombre}`
                            : (i.product_name || 'Promoción'),
                        price: parseFloat(i.precio_unitario),
                        quantity: i.cantidad,
                        valor_producto: i.precio_unitario,
                    };
                }
                if (i.gramos != null && Number(i.gramos) > 0) {
                    return {
                        id: i.producto_id,
                        nombre: i.product_name,
                        price: parseFloat(i.precio_total),
                        quantity: 1,
                        venta_por_peso: true,
                        grams: Number(i.gramos),
                        valor_producto: i.precio_unitario,
                    };
                }
                return {
                    id: i.producto_id,
                    nombre: i.product_name,
                    price: parseFloat(i.precio_unitario),
                    quantity: i.cantidad,
                    valor_producto: i.precio_unitario,
                };
            });

            setCart(cartItems);
            setActivePreventaId(data.id);
            if (data.usuario_id && data.vendedor_name) {
                setSelectedVendedor({ id: data.usuario_id, name: data.vendedor_name });
            }
            setBoletaCargoEnvase(false);
            setBoletaDescuentoDevolucionEnvase(false);
            setShowPreventaModal(false);
            showToast(`Preventa #${data.id} cargada correctamente`, 'success');
            setSearchTerm(''); // Clear search after loading preventa
        } catch (err) {
            console.error(err);
            showToast('Error al cargar la preventa', 'error');
        }
    };

    // Helpers
    const authHeader = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token')}` });
    const calculateCartSubtotal = () =>
        cart.reduce((sum, item) => {
            if (item.venta_por_peso) return sum + (item.price || 0);
            return sum + item.price * item.quantity;
        }, 0);

    /** Total impreso y cobrado redondeado a múltiplos de $10 (ej. 2814→2810, 546→550). */
    const roundBoletaClp = (n) => {
        const x = Math.round(Number(n) || 0);
        return Math.max(0, Math.round(x / 10) * 10);
    };

    const calculateTotal = () => {
        let t = calculateCartSubtotal();
        if (opcionesSesionCajaVentasActivas) {
            if (boletaCargoEnvase) t += ENVASE_TICKET_MONTO_CLP;
            if (boletaDescuentoDevolucionEnvase) t -= ENVASE_TICKET_MONTO_CLP;
        }
        return roundBoletaClp(Math.max(0, t));
    };
    const formatCurrency = (amount) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(amount);

    /** Thermal-style “corte de turno” after closing register (76mm-oriented HTML print). */
    const printCorteTurno = (corte) => {
        if (!corte?.resumen_cierre) return;
        const printWindow = window.open('', '_blank', 'width=360,height=900');
        if (!printWindow) {
            showToast('Habilite ventanas emergentes para imprimir el corte de turno.', 'error');
            return;
        }
        const esc = (s) =>
            String(s ?? '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;');
        const fmtDt = (v) => {
            if (v == null || v === '') return '—';
            const d = v instanceof Date ? v : new Date(v);
            return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'medium' });
        };
        const r = corte.resumen_cierre;
        const egresosVueltoEfectivo = r.egresos_vuelto_efectivo || 0;
        const egresosVueltoTarjeta = r.egresos_vuelto_tarjeta || 0;
        // Giro deducted once in total ventas reales (single line on ticket).
        const totalVentasReales = resolveTotalVentasReales(corte);
        const cuadre = corte.cuadre_ventas_reales || {};
        const redondeoBoleta = Number(cuadre.redondeo_boleta) || 0;
        const envaseNetoBoleta = Number(cuadre.envase_neto_boleta) || 0;

        let extraVueltosRows = '';
        if (egresosVueltoEfectivo > 0) {
            extraVueltosRows += `<tr><td class="col-l" style="padding-left:8px; font-style:italic;">(-) Vuelto en efectivo</td><td class="col-r">-${formatCurrency(egresosVueltoEfectivo)}</td></tr>`;
        }
        if (egresosVueltoTarjeta > 0) {
            extraVueltosRows += `<tr><td class="col-l" style="padding-left:8px; font-style:italic;">(-) Giro tarjeta (efectivo)</td><td class="col-r">-${formatCurrency(egresosVueltoTarjeta)}</td></tr>`;
        }

        const formaRows = (corte.ventas_por_forma_pago || [])
            .map(
                (row) =>
                    `<tr><td class="col-l">${esc(row.forma)}</td><td class="col-r">${formatCurrency(row.monto)}</td></tr>`
            )
            .join('');
        const catRows = (corte.ventas_por_categoria || [])
            .map(
                (row) =>
                    `<tr><td class="col-l">${esc(row.categoria)}</td><td class="col-r">${formatCurrency(row.monto)}</td></tr>`
            )
            .join('');
        const contadoLine =
            r.efectivo_contado != null && Number.isFinite(Number(r.efectivo_contado))
                ? `<div class="row"><span class="col-l">Efectivo contado</span><span class="col-r">${formatCurrency(r.efectivo_contado)}</span></div>`
                : '';
        const forzadoBlock =
            r.cierre_forzado && r.diferencia_efectivo != null
                ? `<div class="meta" style="font-weight:800;text-align:center;margin:8px 0">*** CIERRE CON DESCUADRE ***</div>` +
                  `<div class="row"><span class="col-l">Efectivo esperado</span><span class="col-r">${formatCurrency(r.efectivo_esperado_en_caja ?? r.total_caja_mas_base)}</span></div>` +
                  `<div class="row bold"><span class="col-l">Diferencia</span><span class="col-r">${formatCurrency(r.diferencia_efectivo)}</span></div>`
                : '';
        const html =
            '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Corte de turno</title>' +
            '<style>' +
            '@page { margin: 4mm; }' +
            'body { font-family: ui-monospace, "Cascadia Code", Consolas, monospace; width: 72mm; margin: 0 auto; padding: 2mm; font-size: 11px; line-height: 1.35; color: #000; }' +
            'h1 { text-align: center; font-size: 13px; margin: 0 0 6px; letter-spacing: 0.5px; }' +
            '.meta { margin: 2px 0; font-size: 10px; }' +
            '.sep { border: none; border-top: 1px dashed #000; margin: 8px 0; }' +
            '.sec { font-weight: 700; text-align: center; margin: 10px 0 6px; font-size: 11px; }' +
            'table { width: 100%; border-collapse: collapse; }' +
            'td { padding: 2px 0; vertical-align: top; }' +
            '.col-l { text-align: left; padding-right: 4px; }' +
            '.col-r { text-align: right; white-space: nowrap; font-weight: 600; }' +
            '.bold { font-weight: 800; }' +
            '.dash { border: none; border-top: 1px dashed #000; margin: 6px 0; }' +
            '.row { display: flex; justify-content: space-between; gap: 6px; margin: 3px 0; font-size: 10px; }' +
            '</style></head><body>' +
            `<h1>${esc(corte.titulo || 'CORTE DE TURNO')}</h1>` +
            (corte.fecha_operacional
                ? `<div class="meta" style="font-weight:700">Día operacional: ${esc(
                      new Date(corte.fecha_operacional + 'T12:00:00').toLocaleDateString('es-CL')
                  )}</div>`
                : '') +
            `<div class="meta">Desde ${esc(fmtDt(corte.desde))}</div>` +
            `<div class="meta">Hasta ${esc(fmtDt(corte.hasta))}</div>` +
            `<div class="meta">Cajero: ${esc(corte.caja_nombre || '—')}</div>` +
            (corte.usuario_nombre ? `<div class="meta">Operador: ${esc(corte.usuario_nombre)}</div>` : '') +
            '<hr class="sep" />' +
            '<div class="sec">VENTA POR FORMA DE PAGO</div>' +
            '<table><tbody>' +
            formaRows +
            extraVueltosRows +
            `<tr class="bold"><td class="col-l">Total ventas reales (${corte.cantidad_ventas || 0} vtas)</td><td class="col-r">${formatCurrency(totalVentasReales)}</td></tr>` +
            '</tbody></table>' +
            '<hr class="sep" />' +
            '<div class="sec">RESUMEN DE CIERRE</div>' +
            `<div class="row"><span class="col-l">Total efectivo turno</span><span class="col-r">${formatCurrency(r.total_efectivo_turno)}</span></div>` +
            `<div class="row"><span class="col-l">Total tarjeta digitado</span><span class="col-r">${formatCurrency(r.total_tarjeta_digitado)}</span></div>` +
            `<div class="row"><span class="col-l">Ingresos a caja</span><span class="col-r">${formatCurrency(r.ingresos_caja)}</span></div>` +
            (r.ingresos_pago_cuenta > 0
                ? `<div class="row"><span class="col-l" style="padding-left:8px">Pagos cuenta (efectivo)</span><span class="col-r">${formatCurrency(r.ingresos_pago_cuenta)}</span></div>`
                : '') +
            (r.pagos_cuenta_tarjeta_externa > 0
                ? `<div class="row"><span class="col-l" style="padding-left:8px">Pagos cuenta (tarj. externa)*</span><span class="col-r">${formatCurrency(r.pagos_cuenta_tarjeta_externa)}</span></div>`
                : '') +
            (r.ingresos_inyeccion_efectivo > 0
                ? `<div class="row"><span class="col-l" style="padding-left:8px">Inyección efectivo</span><span class="col-r">${formatCurrency(r.ingresos_inyeccion_efectivo)}</span></div>`
                : '') +
            (r.egresos_retiro_seguridad > 0
                ? `<div class="row"><span class="col-l" style="padding-left:8px">Retiro seguridad</span><span class="col-r">-${formatCurrency(r.egresos_retiro_seguridad)}</span></div>`
                : '') +
            '<div class="row"><span class="col-l">Egresos de caja</span><span class="col-r"></span></div>' +
            `<div class="row"><span class="col-l" style="padding-left:8px">Vuelto en efectivo</span><span class="col-r">${formatCurrency(r.egresos_vuelto_efectivo || 0)}</span></div>` +
            `<div class="row"><span class="col-l" style="padding-left:8px">Vuelto/giro con tarjeta</span><span class="col-r">${formatCurrency(r.egresos_vuelto_tarjeta || 0)}</span></div>` +
            `<div class="row"><span class="col-l" style="padding-left:8px">Total egresos</span><span class="col-r">${formatCurrency(r.egresos_caja)}</span></div>` +
            `<div class="row bold"><span class="col-l">Total caja</span><span class="col-r">${formatCurrency(r.total_caja)}</span></div>` +
            `<div class="row"><span class="col-l">Monto base caja</span><span class="col-r">${formatCurrency(r.monto_base_caja)}</span></div>` +
            contadoLine +
            forzadoBlock +
            '<div class="dash"></div>' +
            `<div class="row bold" style="font-size:12px"><span class="col-l">Total caja + base</span><span class="col-r">${formatCurrency(r.total_caja_mas_base)}</span></div>` +
            '<div class="dash"></div>' +
            '<div class="sec">VENTAS POR CATEGORÍA</div>' +
            `<table><tbody>${catRows}` +
            (Math.abs(envaseNetoBoleta) >= 1
                ? `<tr><td class="col-l" style="padding-left:8px; font-style:italic;">Envases en boleta (neto)</td><td class="col-r">${formatCurrency(envaseNetoBoleta)}</td></tr>`
                : '') +
            (Math.abs(redondeoBoleta) >= 1
                ? `<tr><td class="col-l" style="padding-left:8px; font-style:italic;">Redondeo boleta ($10)</td><td class="col-r">${formatCurrency(redondeoBoleta)}</td></tr>`
                : '') +
            `<tr class="bold"><td class="col-l">Suma cat. + ajustes</td><td class="col-r">${formatCurrency(totalVentasReales)}</td></tr>` +
            '</tbody></table>' +
            '<script>window.onload=function(){window.print();setTimeout(function(){window.close();},500);}</script>' +
            '</body></html>';
        printWindow.document.write(html);
        printWindow.document.close();
    };

    /** Imprime corte de turno con datos al momento (no cierra la sesión). */
    const handleImprimirCorteTurno = async () => {
        try {
            const res = await fetch(`${API_URL}/api/cajas/cierre-resumen`, { headers: authHeader() });
            if (res.status === 401) {
                logout();
                return;
            }
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                showToast(err.error || 'No se pudo obtener el resumen para imprimir.', 'error');
                return;
            }
            const data = await res.json();
            if (data.corte_turno) {
                printCorteTurno(data.corte_turno);
            } else {
                showToast('No hay datos de corte para imprimir.', 'error');
            }
        } catch (e) {
            console.error(e);
            showToast('Error al preparar la impresión.', 'error');
        }
    };

    const productThumbSrc = (p) => {
        const u = p?.foto_url && String(p.foto_url).trim();
        if (!u) return null;
        return u.startsWith('http') ? u : `${API_URL}${u}`;
    };

    const addToCart = async (product, clearSearch = true) => {
        // Productos por peso: mostrar modal para ingresar gramos
        if (product.venta_por_peso) {
            setPesoProductToAdd(product);
            setPesoGramsInput('');
            setShowPesoModal(true);
            return;
        }

        const existing = cart.find(item => item.id === product.id && !item.venta_por_peso);
        const newQuantity = existing ? existing.quantity + 1 : 1;
        const stockActual = product.stock_actual || 0;

        // Si el sistema marca 0 (p. ej. inventario mal cargado por sucursal), igual se puede vender mercadería física en caja/mostrador
        if (stockActual > 0 && newQuantity > stockActual) {
            showToast(`Stock insuficiente según sistema. Disponible: ${stockActual} unidades`, 'error');
            return;
        }

        setCart(prev => {
            const existing = prev.find(item => item.id === product.id && !item.venta_por_peso);
            if (existing) {
                return prev.map(item => item.id === product.id && !item.venta_por_peso ? { ...item, quantity: item.quantity + 1 } : item);
            }
            return [...prev, { ...product, quantity: 1, price: parseFloat(product.valor_producto) }];
        });
        // Clear search and refocus for faster scanning workflow
        if (clearSearch) {
            setSearchTerm('');
            setProducts([]);
            setTimeout(() => searchInputRef.current?.focus(), 50);
        }
    };

    const confirmAddPesoToCart = () => {
        if (!pesoProductToAdd) return;
        const grams = parseInt(pesoGramsInput, 10) || 0;
        if (grams <= 0) {
            showToast('Ingrese una cantidad válida en gramos', 'error');
            return;
        }
        const precioPorKilo = parseFloat(pesoProductToAdd.precio_por_kilo || pesoProductToAdd.valor_producto) || 0;
        const stockActual = pesoProductToAdd.stock_actual || 0;
        if (stockActual > 0 && grams > stockActual) {
            showToast(`Stock insuficiente. Disponible: ${stockActual} g`, 'error');
            return;
        }
        const price = Math.round((precioPorKilo / 1000) * grams);
        const cartItem = {
            ...pesoProductToAdd,
            id: pesoProductToAdd.id,
            quantity: 1,
            grams,
            price,
            venta_por_peso: true,
            cartItemId: `peso-${pesoProductToAdd.id}-${Date.now()}`
        };
        setCart(prev => [...prev, cartItem]);
        setShowPesoModal(false);
        setPesoProductToAdd(null);
        setPesoGramsInput('');
        setSearchTerm('');
        setProducts([]);
        setTimeout(() => searchInputRef.current?.focus(), 50);
    };

    const getCartItemKey = (item) => item.cartItemId || item.id;
    const updateQuantity = (cartItemKey, change) => {
        setCart(prev => {
            const item = prev.find(i => getCartItemKey(i) === cartItemKey);
            if (!item) return prev;
            if (item.venta_por_peso) {
                if (change < 0) return prev.filter(i => getCartItemKey(i) !== cartItemKey);
                return prev;
            }
            const newQuantity = item.quantity + change;
            if (newQuantity < 1) return prev.filter(i => getCartItemKey(i) !== cartItemKey);
            if (item.isPromo) return prev.map(i => getCartItemKey(i) === cartItemKey ? { ...i, quantity: newQuantity } : i);
            const stockActual = item.stock_actual || 0;
            if (stockActual > 0 && newQuantity > stockActual) {
                showToast(`Stock insuficiente. Disponible: ${stockActual} unidades`, 'error');
                return prev;
            }
            return prev.map(i => getCartItemKey(i) === cartItemKey ? { ...i, quantity: newQuantity } : i);
        });
    };

    const removeFromCart = (cartItemId) => setCart(prev => prev.filter(item => (item.cartItemId || item.id) !== cartItemId));

    const addPromoToCart = async (promo, selectedItem = null, addFullPack = false) => {
        try {
            const sucursalId = cajaSession?.session?.sucursal_id || 1;
            const res = await fetch(`${API_URL}/api/promotions/${promo.id}?sucursal_id=${sucursalId}`, { headers: authHeader() });
            if (res.status === 401) { logout(); return; }
            if (!res.ok) {
                showToast('Error al cargar la promoción', 'error');
                return;
            }
            const promoDetail = await res.json();
            const items = promoDetail.items || [];

            // If multiple items, no selection yet, and not full pack: show selection modal
            if (items.length > 1 && !selectedItem && !addFullPack) {
                const itemsWithStock = items.filter(i => (parseInt(i.stock_actual) || 0) >= (parseInt(i.cantidad) || 1));
                if (itemsWithStock.length === 0) {
                    showToast('No hay stock suficiente para ninguna opción de esta promoción', 'error');
                    return;
                }
                setPromoSelectionModal({ promo, detail: promoDetail, allHaveStock: items.every(i => (parseInt(i.stock_actual) || 0) >= (parseInt(i.cantidad) || 1)) });
                return;
            }

            // Full pack: all items must have stock
            if (addFullPack) {
                const allOk = items.every(i => (parseInt(i.stock_actual) || 0) >= (parseInt(i.cantidad) || 1));
                if (!allOk) {
                    showToast('No hay stock suficiente para el pack completo', 'error');
                    return;
                }
            } else {
                // Single item or selected item: validate stock
                const toCheck = selectedItem 
                    ? items.find(i => i.producto_id === selectedItem.producto_id) 
                    : items[0];
                const stockActual = parseInt(toCheck?.stock_actual) || 0;
                const cantidadReq = parseInt(toCheck?.cantidad) || 1;
                if (stockActual < cantidadReq) {
                    showToast('No hay stock suficiente para esta opción', 'error');
                    return;
                }
            }

            const promoCartId = selectedItem 
                ? `promo-${promo.id}-${selectedItem.producto_id}` 
                : `promo-${promo.id}`;
            const existing = cart.find(item => item.id === promoCartId);
            const toCheck = selectedItem ? items.find(i => i.producto_id === selectedItem.producto_id) : null;
            const displayName = selectedItem && toCheck 
                ? `Promo: ${promo.nombre} (${toCheck.nombre})` 
                : `Promo: ${promo.nombre}`;

            setCart(prev => {
                const newItem = {
                    id: promoCartId,
                    isPromo: true,
                    promocion_id: promo.id,
                    nombre: displayName,
                    price: parseFloat(promo.monto),
                    quantity: 1,
                    valor_producto: promo.monto,
                    ...(selectedItem && { promo_selected_item: selectedItem })
                };
                if (existing) {
                    return prev.map(item =>
                        item.id === promoCartId ? { ...item, quantity: item.quantity + 1 } : item
                    );
                }
                return [...prev, newItem];
            });
            setShowPromocionesModal(false);
            setPromoSelectionModal(null);
            setPromoSearchTerm('');
            showToast(`Promoción "${promo.nombre}" agregada al carrito`, 'success');
        } catch (err) {
            console.error(err);
            showToast('Error al agregar promoción', 'error');
        }
    };

    const filteredPromotions = promotions.filter(p =>
        !promoSearchTerm.trim() ||
        p.nombre?.toLowerCase().includes(promoSearchTerm.toLowerCase()) ||
        p.descripcion?.toLowerCase().includes(promoSearchTerm.toLowerCase())
    );

    const total = calculateTotal();
    const vueltoManualChequeoPago =
        isCajero && cajeroPuedeEjecutarCobro ? vueltoEfectivo : 0;
    const chequeoVueltoEnPago =
        showPaymentModal && requiereCajaOperativa && cajaSession?.isOpen
            ? computeEfectivoFaltanteParaVuelto(
                cajaSession.efectivo_disponible_en_caja,
                total,
                paymentData,
                vueltoManualChequeoPago
            )
            : null;
    const faltaEfectivoParaVuelto = chequeoVueltoEnPago?.falta > 0;

    // Determinar si el cajero debe abrir caja
    // Solo mostrar la pantalla si:
    // 1. Es cajero
    // 2. El estado ya se cargó (no está cargando)
    // 3. No tiene caja abierta (cajaSession es null O cajaSession.isOpen es false)
    // Pero NO mostrar si cajaSession es null y todavía está cargando
    const cajeroMustOpenCaja = requiereCajaOperativa &&
                                !cajaStatusLoading && 
                                cajaSession !== null && 
                                !cajaSession?.isOpen;
    
    // Debug: Log para entender cuándo se muestra la pantalla
    useEffect(() => {
        if (isCajero) {
            console.log('🔍 Evaluando cajeroMustOpenCaja:', {
                isCajero,
                cajaStatusLoading,
                cajaSession,
                cajaSessionIsOpen: cajaSession?.isOpen,
                cajeroMustOpenCaja,
                willShowScreen: cajeroMustOpenCaja && !cajaStatusLoading
            });
        }
    }, [isCajero, cajaStatusLoading, cajaSession, cajeroMustOpenCaja]);

    // Debug: Log del estado completo para depuración
    useEffect(() => {
        if (canCompleteSale) {
            const userRole = user?.role;
            const isAdmin = userRole === ROLE_ADMIN;
            const buttonDisabled =
                cart.length === 0 ||
                loading ||
                (requiereCajaOperativa && !puedeEjecutarCobro) ||
                (isVendedorRole && !activePreventaId);
            console.log('🔍 Estado completo POS:', JSON.stringify({
                userRole,
                isCajero,
                isAdmin,
                isPreventaPrevendedor,
                isVendedorRole,
                canCompleteSale,
                cajaSession: cajaSession ? { isOpen: cajaSession.isOpen, hasSession: !!cajaSession.session } : null,
                cajaSessionIsOpen: cajaSession?.isOpen,
                cajeroMustOpenCaja,
                cajaStatusLoading,
                availableCajas: availableCajas.length,
                cartLength: cart.length,
                loading,
                activePreventaId,
                buttonDisabled,
                buttonDisabledReasons: {
                    cartEmpty: cart.length === 0,
                    loading: loading,
                    cajeroNoCaja: requiereCajaOperativa && !puedeEjecutarCobro,
                    vendedorNoPreventa: isVendedorRole && !activePreventaId
                }
            }, null, 2));
        }
    }, [canCompleteSale, isCajero, isPreventaPrevendedor, isVendedorRole, cajaSession, availableCajas.length, cajaStatusLoading, cart.length, loading, activePreventaId, user, cajeroMustOpenCaja]);

    // Mostrar pantalla de "abrir caja" para cajeros que no tienen caja abierta
    // Solo mostrar cuando el estado ya se cargó y no está cargando
    if (cajeroMustOpenCaja) {
        console.log('🖥️ Mostrando pantalla de abrir caja:', {
            isCajero,
            cajaStatusLoading,
            cajaSession,
            cajaSessionIsOpen: cajaSession?.isOpen,
            cajeroMustOpenCaja,
            availableCajas: availableCajas.length
        });
        return (
            <div className="pos-container relative min-h-screen flex items-center justify-center bg-gray-100">
                {availableCajas.length === 0 ? (
                    <div className="bg-white p-8 rounded-2xl shadow-lg max-w-md text-center">
                        <div className="bg-amber-50 p-4 rounded-full inline-flex mb-4">
                            <Lock size={48} className="text-amber-600" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-800 mb-2">Debe abrir caja para operar</h2>
                        <p className="text-gray-600 mb-6">
                            No hay cajas asignadas a su usuario o no existen sucursales/cajas creadas. Contacte al administrador para que cree sucursales, cajas y le asigne una caja.
                        </p>
                        <a href="/sucursales" className="text-indigo-600 font-medium hover:underline">Ir a Sucursales</a>
                    </div>
                ) : (
                    <div className="fixed inset-0 bg-gray-900/90 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
                        <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-2xl w-full max-w-md border border-gray-100 my-4">
                            <div className="flex flex-col items-center mb-6">
                                <div className="bg-indigo-50 p-4 rounded-full mb-4">
                                    <Unlock size={32} className="text-indigo-600" />
                                </div>
                                <h2 className="text-2xl font-bold text-gray-800">Abrir Caja</h2>
                                <p className="text-gray-500 text-center mt-2 text-sm">Para continuar debe abrir caja e ingresar el monto y la cantidad de billetes/monedas.</p>
                            </div>
                            <form onSubmit={handleOpenCaja} className="flex flex-col gap-5">
                                <div className="form-group">
                                    <label className="text-gray-700 font-semibold mb-1">Seleccionar Caja</label>
                                    <select
                                        required
                                        className="w-full border-gray-300 rounded-lg p-3 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all outline-none"
                                        value={openCajaData.caja_id}
                                        onChange={e => setOpenCajaData({ ...openCajaData, caja_id: e.target.value })}
                                    >
                                        <option value="">Seleccione una caja...</option>
                                        {availableCajas.map(c => (
                                            <option key={c.id} value={c.id}>{c.sucursal_nombre ? `${c.nombre} (${c.sucursal_nombre})` : c.nombre}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="text-gray-700 font-semibold mb-1">Monto en efectivo en la caja registradora</label>
                                    <div className="relative">
                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">$</span>
                                        <input
                                            required
                                            type="number"
                                            min="0"
                                            className="w-full border-gray-300 rounded-lg p-3 pl-10 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all outline-none text-lg font-bold text-gray-800"
                                            placeholder="0"
                                            value={openCajaData.monto_inicial}
                                            onChange={e => setOpenCajaData({ ...openCajaData, monto_inicial: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <DenominacionEfectivo
                                    value={openCajaDesglose}
                                    onChange={(next, total) => {
                                        setOpenCajaDesglose(next);
                                        setOpenCajaData((prev) => ({ ...prev, monto_inicial: String(total) }));
                                    }}
                                    label="Cantidad de billetes y monedas"
                                    accentColor="#4f46e5"
                                    compact
                                    showTotal={false}
                                />
                                <button type="submit" className="w-full bg-indigo-500 text-gray-900 p-4 rounded-xl font-bold hover:bg-indigo-700 shadow-lg">
                                    Abrir Sesión
                                </button>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <>
        <div className="pos-wrapper flex flex-col w-full h-full min-h-0">
            {/* Barra superior: caja abierta + botón cerrar */}
            {canCompleteSale && cajaSession?.isOpen && (
                <div className="flex items-center justify-between px-4 py-2.5 bg-green-50 border-b border-green-200 text-green-800 shrink-0">
                    <div className="flex items-center gap-2">
                        <Unlock size={18} className="text-green-600" />
                        <span className="font-semibold">Caja abierta:</span>
                        <span className="font-bold">
                            {cajaSession?.session?.caja_nombre || 
                             availableCajas.find(c => c.id == cajaSession?.session?.caja_id)?.nombre || 
                             'POS'}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={handleImprimirCorteTurno}
                            className="flex items-center gap-2 px-3 py-1.5 bg-white/80 text-green-900 border border-green-300 rounded-lg hover:bg-white font-medium text-sm transition-colors cursor-pointer"
                            title="Imprimir corte de turno sin cerrar"
                        >
                            <Printer size={16} /> Imprimir corte
                        </button>
                        <button
                            type="button"
                            onClick={openCloseCajaModal}
                            className="flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-medium text-sm transition-colors cursor-pointer"
                            title="Cerrar caja"
                        >
                            <Lock size={16} /> Cerrar Caja
                        </button>
                    </div>
                </div>
            )}
            {cajeroVenBloqueadoPorEstadoLocal && (
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 bg-amber-50 border-b border-amber-300 text-amber-950 shrink-0">
                    <span className="text-sm font-medium">
                        {cajaSession?.sales_blocked_reason ||
                            'Este turno está en una caja o sucursal no habilitada para ventas. Corríjalo antes de cobrar.'}
                    </span>
                    <Link
                        to="/sucursales"
                        className="text-sm font-semibold text-amber-900 underline hover:no-underline whitespace-nowrap"
                    >
                        Ir a Sucursales
                    </Link>
                </div>
            )}
        <div className="pos-container relative flex-1 min-h-0">
            {/* Toast notification - z-[9999] para que aparezca siempre encima de modales */}
            {toast.show && (
                <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-[9999] px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 animate-pulse ${
                    toast.type === 'error' 
                        ? 'bg-red-500 text-white' 
                        : toast.type === 'success' 
                            ? 'bg-green-500 text-white' 
                            : 'bg-yellow-500 text-white'
                }`}>
                    <span className="text-xl">⚠️</span>
                    <span className="font-medium">{toast.message}</span>
                </div>
            )}
            
            <div className="pos-left">
                <div className="search-bar relative">
                    <Search />
                    <input
                        ref={searchInputRef}
                        type="text"
                        placeholder={
                            isPreventaPrevendedor
                                ? 'Escanear código de barras, VND, o nombre (solo productos sin código de barras)...'
                                : isCajero && cajeroPuedeEjecutarCobro
                                    ? canScanVendedorBarcode
                                        ? 'Escanear código de barras, PRV, VND, ENV… o nombre (solo sin código de barras)...'
                                        : 'Escanear código de barras, PRV, ENV… o nombre (solo sin código de barras)...'
                                    : canScanVendedorBarcode
                                        ? 'Escanear código de barras, PRV, VND… o nombre (solo sin código de barras)...'
                                        : 'Escanear código de barras / PRV… o nombre (solo sin código de barras)...'
                        }
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        autoFocus
                        title={
                            isPreventaPrevendedor
                                ? 'Escanee el código de barras; el nombre solo encuentra productos sin código de barras.'
                                : 'Escanee el código de barras; el nombre solo encuentra productos sin código de barras. Caja puede escanear PRV y ENV.'
                        }
                    />
                    {/* Tooltip de ayuda para búsqueda */}
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 group">
                        <HelpCircle size={16} className="text-gray-400 hover:text-gray-600 cursor-help" />
                        <div className="absolute right-0 top-full mt-1 w-64 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg z-50 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                            <div className="font-semibold mb-1">💡 Consejos de búsqueda:</div>
                            <ul className="list-disc list-inside space-y-1">
                                <li>Prioridad: escanee el código de barras del producto</li>
                                <li>El nombre solo lista productos sin código de barras en el sistema</li>
                                {!isPreventaPrevendedor && (
                                    <li>Escanee código de preventa (PRV00000123)</li>
                                )}
                                {canScanVendedorBarcode && (
                                    <li>
                                        {isCajero || isAdmin
                                            ? 'VND opcional (si no escanea, la venta se atribuye a usted)'
                                            : 'Escanee código de vendedor (VND + número, ej. VND42)'}
                                    </li>
                                )}
                                {isCajero && cajeroPuedeEjecutarCobro && (
                                    <li>Escanee código ENV del ticket para devolver dinero del envase</li>
                                )}
                                <li>Haga clic en un producto para agregarlo al carrito</li>
                            </ul>
                            <div className="absolute top-0 right-4 transform -translate-y-full">
                                <div className="border-4 border-transparent border-b-gray-900"></div>
                            </div>
                        </div>
                    </div>
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            <X size={18} />
                        </button>
                    )}
                </div>
                <p className="text-xs text-gray-500 shrink-0 -mt-1 mb-0 px-0.5">
                    Debajo: lista de productos encontrados (foto si existe en el sistema).
                </p>
                <div className="product-list">
                    {loading ? (
                        <div className="p-8 text-center text-gray-500">Buscando productos...</div>
                    ) : products.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                            {searchTerm
                                ? 'No se encontraron productos'
                                : 'Escanee el código de barras, o escriba nombre solo para productos sin código de barras'}
                        </div>
                    ) : (
                        <>
                            {promotionsInSearch.map(promo => (
                                <div
                                    key={`promo-${promo.id}`}
                                    className="pos-product-card pos-product-card--promo border-amber-200 bg-amber-50/30 cursor-pointer"
                                    onClick={() => addPromoToCart(promo)}
                                >
                                    <span className="text-xs font-bold text-amber-600 uppercase tracking-wide">Promo</span>
                                    <h3>{promo.nombre}</h3>
                                    <p className="price text-amber-700">{formatCurrency(promo.monto)}</p>
                                    <div className="stock-badge bg-amber-100 text-amber-800">Promoción</div>
                                </div>
                            ))}
                            {products.map(product => {
                                const thumb = productThumbSrc(product);
                                return (
                                <div key={product.id} className="pos-product-card" onClick={() => addToCart(product)}>
                                    <div className="pos-product-thumb" aria-hidden>
                                        {thumb ? (
                                            <img src={thumb} alt="" loading="lazy" />
                                        ) : (
                                            <span className="pos-product-thumb-placeholder">
                                                <Package size={22} strokeWidth={1.5} />
                                            </span>
                                        )}
                                    </div>
                                    <div className="pos-product-card-body">
                                        <h3>{product.nombre}</h3>
                                        <p className="price">
                                            {product.venta_por_peso
                                                ? `${formatCurrency(product.precio_por_kilo || product.valor_producto)}/kg`
                                                : formatCurrency(product.valor_producto)}
                                        </p>
                                        <div className={`stock-badge ${
                                            (product.stock_actual ?? 0) <= 0
                                                ? 'bg-red-100 text-red-700'
                                                : product.stock_actual <= product.stock_minimo
                                                    ? 'bg-yellow-100 text-yellow-700'
                                                    : ''
                                        }`}>
                                            Stock: {product.venta_por_peso ? `${Math.max(0, product.stock_actual ?? 0)}g` : Math.max(0, product.stock_actual ?? 0)}
                                        </div>
                                    </div>
                                </div>
                            );})}
                        </>
                    )}
                </div>
            </div>

            <div className="pos-right">
                <div className="pos-cart-toolbar flex justify-between items-center border-b bg-gray-50 shrink-0">
                    <div className="flex items-center gap-2 min-w-0 py-2 px-3">
                        <h2 className="pos-cart-title flex items-center gap-2 m-0 border-none p-0 text-sm font-semibold text-gray-800">
                            <ShoppingCart size={16} /> Venta actual
                        </h2>
                        {/* Botón de ayuda sutil - solo hover */}
                        <div className="relative group">
                            <button
                                className="p-1 text-gray-400 hover:text-blue-600 transition-colors rounded-full hover:bg-blue-50"
                                title="Ver ayuda"
                            >
                                <HelpCircle size={16} />
                            </button>
                            {/* Tooltip de ayuda solo al hacer hover */}
                            <div className="absolute left-0 top-full mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-xl z-50 p-3 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                <div className="text-sm text-gray-700 space-y-2.5">
                                    <div className="font-semibold text-gray-900 mb-2">Guía rápida</div>
                                    <div>
                                        <div className="font-medium mb-0.5 text-xs">1️⃣ Agregar productos</div>
                                        <div className="text-xs text-gray-600">Escanee códigos o busque por nombre solo si el producto no tiene código de barras; haga clic para agregar al carrito.</div>
                                    </div>
                                    {isCajero && (
                                        <div>
                                            <div className="font-medium mb-0.5 text-xs">2️⃣ Abrir caja</div>
                                            <div className="text-xs text-gray-600">Debe abrir una caja antes de completar ventas</div>
                                        </div>
                                    )}
                                    {isPreventaPrevendedor && (
                                        <div>
                                            <div className="font-medium mb-0.5 text-xs">2️⃣ Generar preventa</div>
                                            <div className="text-xs text-gray-600">Cuando el cliente termine, use Generar Preventa para imprimir el ticket con código PRV para caja.</div>
                                        </div>
                                    )}
                                    {isVendedorRole && (
                                        <div>
                                            <div className="font-medium mb-0.5 text-xs">2️⃣ Cargar preventa</div>
                                            <div className="text-xs text-gray-600">La preventa la genera el perfil Preventa; usted cargue con &quot;Cargar Preventa&quot; o escanee PRV para cobrar.</div>
                                        </div>
                                    )}
                                    {(canCompleteSale || isVendedorRole) && (
                                        <div>
                                            <div className="font-medium mb-0.5 text-xs">3️⃣ Completar venta</div>
                                            <div className="text-xs text-gray-600">
                                                {isPreventaPrevendedor
                                                    ? '(Solo caja / admin)'
                                                    : isCajero || isAdmin
                                                        ? 'Venta directa: agregue productos y pulse Completar Venta (VND opcional). También puede cargar una preventa PRV.'
                                                        : 'Haga clic cuando el botón esté habilitado'}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="absolute top-0 left-4 transform -translate-y-full">
                                    <div className="border-4 border-transparent border-b-white"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Botón Promociones + Cargar Preventa */}
                    <div className="flex gap-1.5 items-center">
                        <button
                            type="button"
                            onClick={() => { fetchPromotions(); setShowPromocionesModal(true); }}
                            className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 transition-colors cursor-pointer"
                            title="Ver y agregar promociones al carrito"
                        >
                            <TrendingUp size={14} /> <span className="hidden sm:inline">Promociones</span>
                        </button>
                        {/* Indicador de estado de caja para cajeros - más compacto */}
                        {isCajero && (
                            <div className="relative group">
                                <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                                    cajaSession?.isOpen 
                                        ? 'bg-green-50 text-green-700 border border-green-200' 
                                        : 'bg-red-50 text-red-700 border border-red-200'
                                }`}>
                                    {cajaSession?.isOpen ? (
                                        <>
                                            <Unlock size={12} />
                                            <span className="hidden sm:inline">Abierta</span>
                                        </>
                                    ) : (
                                        <>
                                            <Lock size={12} />
                                            <span className="hidden sm:inline">Cerrada</span>
                                        </>
                                    )}
                                </div>
                                {/* Tooltip explicativo */}
                                {!cajeroPuedeEjecutarCobro && (
                                    <div className="absolute right-0 top-full mt-1 w-56 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg z-50 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                        <div className="font-semibold mb-1">⚠️ Caja no disponible para cobro</div>
                                        <div>
                                            {cajeroVenBloqueadoPorEstadoLocal
                                                ? (cajaSession?.sales_blocked_reason || 'Active la sucursal y la caja en administración.')
                                                : 'Debe abrir una caja antes de poder completar ventas.'}
                                        </div>
                                        <div className="absolute top-0 right-4 transform -translate-y-full">
                                            <div className="border-4 border-transparent border-b-gray-900"></div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {canLoadPreventaInPos && (
                            <button
                                onClick={() => { fetchPreventas(); setShowPreventaModal(true); }}
                                className="px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded text-xs font-medium hover:bg-indigo-100 transition-colors"
                                title="Cargar preventa pendiente para cobrar"
                            >
                                <span className="hidden sm:inline">Cargar </span>Preventa
                            </button>
                        )}
                    {canCompleteSale && cajaSession?.isOpen && (
                        <>
                            {cajeroPuedeEjecutarCobro && (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setMovimientoEfectivoTipo('inyeccion_efectivo');
                                            setMovimientoEfectivoMonto('');
                                            setMovimientoEfectivoDesglose(createEmptyDesglose());
                                            setMovimientoEfectivoSugerido(null);
                                            setShowMovimientoEfectivoModal(true);
                                        }}
                                        className="px-2 py-1 bg-emerald-50 text-emerald-800 rounded text-xs font-medium hover:bg-emerald-100 transition-colors"
                                        title="Inyectar efectivo al cajón durante el turno"
                                    >
                                        <ArrowDownToLine size={14} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setMovimientoEfectivoTipo('retiro_efectivo_seguridad');
                                            setMovimientoEfectivoMonto('');
                                            setMovimientoEfectivoDesglose(createEmptyDesglose());
                                            setMovimientoEfectivoSugerido(null);
                                            setShowMovimientoEfectivoModal(true);
                                        }}
                                        className="px-2 py-1 bg-amber-50 text-amber-900 rounded text-xs font-medium hover:bg-amber-100 transition-colors"
                                        title="Retirar efectivo del cajón (seguridad / bóveda)"
                                    >
                                        <Shield size={14} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { setPagoCuentaMonto(''); setPagoCuentaModo('efectivo'); setShowPagoCuentaModal(true); }}
                                        className="px-2 py-1 bg-sky-50 text-sky-800 rounded text-xs font-medium hover:bg-sky-100 transition-colors"
                                        title="Registrar pago de cuenta/servicio (efectivo o tarjeta externa)"
                                    >
                                        <Wallet size={14} />
                                    </button>
                                </>
                            )}
                            <button
                                type="button"
                                onClick={handleImprimirCorteTurno}
                                className="px-2 py-1 bg-emerald-50 text-emerald-800 rounded text-xs font-medium hover:bg-emerald-100 transition-colors"
                                title="Imprimir corte de turno (sin cerrar)"
                            >
                                <Printer size={14} />
                            </button>
                            <button
                                onClick={openCloseCajaModal}
                                className="px-2 py-1 bg-red-50 text-red-700 rounded text-xs font-medium hover:bg-red-100 transition-colors"
                                title="Cerrar Caja"
                            >
                                <Lock size={14} />
                            </button>
                        </>
                    )}
                    {/* Botón para abrir caja cuando no tiene caja abierta */}
                    {canCompleteSale && !cajaSession?.isOpen && (
                        <button
                            onClick={() => setShowOpenCajaModal(true)}
                            className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors shadow-sm flex items-center gap-1.5"
                            title="Abrir Caja - requerido para completar ventas"
                        >
                            <Unlock size={14} />
                            <span className="hidden sm:inline">Abrir Caja</span>
                        </button>
                    )}
                    </div>
                </div>

                {activePreventaId && (
                    <div className="bg-green-50 px-4 py-2 border-b border-green-200 text-green-800 text-sm flex justify-between items-center">
                        <span className="flex items-center gap-2">
                            <FileText size={16} />
                            <span>
                                Preventa <strong>#{activePreventaId}</strong> cargada —{' '}
                                {canCompleteSale || isVendedorRole
                                    ? 'Ajuste cantidades con +/− si corresponde; luego complete la venta.'
                                    : 'Lista para revisión'}
                            </span>
                        </span>
                        <button 
                            onClick={() => { 
                                if (confirm('¿Desea descartar la preventa cargada?')) {
                                    setActivePreventaId(null); 
                                    setCart([]); 
                                }
                            }} 
                            className="text-green-600 hover:text-green-900"
                            title="Descartar preventa"
                        >
                            <X size={14} />
                        </button>
                    </div>
                )}

                <div className="cart-items">
                    {cart.length === 0 ? (
                        <div className="empty-cart-container">
                            <p className="empty-cart" style={{ writingMode: 'horizontal-tb', textOrientation: 'mixed' }}>El carrito está vacío</p>
                            <div className="relative inline-flex group">
                                <button 
                                    className="text-gray-400 hover:text-blue-600 transition-colors p-2 rounded-full hover:bg-blue-50"
                                    title="Ver ayuda"
                                >
                                    <Info size={18} />
                                </button>
                                {/* Tooltip de ayuda sutil al hacer hover */}
                                <div className="absolute left-1/2 top-full mt-2 transform -translate-x-1/2 w-72 bg-white border border-gray-200 rounded-lg shadow-xl z-50 p-4 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                    <div className="text-sm text-gray-700">
                                        <div className="font-semibold mb-2 text-gray-900">Pasos para completar una venta:</div>
                                        <div className="flex flex-col gap-1.5 text-xs">
                                            <div>1️⃣ <strong>Agregar productos:</strong> Busque productos arriba o haga clic en ellos</div>
                                            {isCajero && !cajaSession?.isOpen && (
                                                <div>2️⃣ <strong>Abrir caja:</strong> Debe abrir una caja antes de completar ventas</div>
                                            )}
                                            {isCajero && cajeroVenBloqueadoPorEstadoLocal && (
                                                <div>2️⃣ <strong>Sucursal o caja inactiva:</strong> Actívelos en Gestión de sucursales o en Cajas</div>
                                            )}
                                            {isCajero && cajeroPuedeEjecutarCobro && (
                                                <div>2️⃣ <strong>Caja lista:</strong> Puede registrar el cobro</div>
                                            )}
                                            <div>3️⃣ <strong>Completar venta:</strong> Haga clic en "Completar Venta"</div>
                                        </div>
                                    </div>
                                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-full">
                                        <div className="border-4 border-transparent border-b-white"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        cart.map((item, idx) => (
                            <div key={getCartItemKey(item) || idx} className="cart-item">
                                <div className="item-info">
                                    <h4>{item.nombre}{item.venta_por_peso ? ` (${item.grams}g)` : ''}</h4>
                                    <p>{formatCurrency(item.venta_por_peso ? item.price : item.price * item.quantity)}</p>
                                </div>
                                <div className="item-controls">
                                    <button onClick={() => updateQuantity(getCartItemKey(item), -1)}><Minus size={16} /></button>
                                    <span>{item.venta_por_peso ? `${item.grams}g` : item.quantity}</span>
                                    <button onClick={() => updateQuantity(getCartItemKey(item), 1)}><Plus size={16} /></button>
                                    <button onClick={() => removeFromCart(getCartItemKey(item))} className="remove-btn"><Trash2 size={16} /></button>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <div className="pos-footer pos-footer--compact">
                    {showVendedorAssignment && (
                        <div className={`flex items-center justify-between gap-2 mb-2 px-2 py-1.5 rounded-lg text-sm ${
                            selectedVendedor
                                ? 'bg-indigo-50 dark:bg-indigo-900/30'
                                : saleRequiresVnd
                                    ? 'bg-amber-50 dark:bg-amber-900/30 border border-amber-200'
                                    : 'bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700'
                        }`}>
                            <span className={
                                selectedVendedor
                                    ? 'text-indigo-700 dark:text-indigo-300'
                                    : saleRequiresVnd
                                        ? 'text-amber-800 dark:text-amber-200'
                                        : 'text-gray-600 dark:text-gray-300'
                            }>
                                <strong>Vendedor:</strong>{' '}
                                {selectedVendedor
                                    ? selectedVendedor.name
                                    : saleRequiresVnd
                                        ? 'Escanee código VND para cobrar'
                                        : 'Opcional — sin VND se atribuye a usted'}
                            </span>
                            {selectedVendedor && (
                                <button
                                    type="button"
                                    onClick={() => setSelectedVendedor(null)}
                                    className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
                                    title="Quitar vendedor asignado"
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>
                    )}
                    <div className="total-section">
                        <span className="total-label">Total:</span>
                        <span className="total-amount">{formatCurrency(total)}</span>
                    </div>
                    {isPreventaPrevendedor && (
                        <button
                            type="button"
                            disabled={loading}
                            onClick={printEnvasesTicket}
                            className="w-full mb-2 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold bg-amber-50 text-amber-900 border border-amber-200 hover:bg-amber-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title={`Imprime papel orientativo (${formatCurrency(ENVASE_TICKET_MONTO_CLP)}). El cobro lo registra caja al cobrar.`}
                        >
                            <Ticket size={18} strokeWidth={2.25} />
                            Imprimir ticket envases ({formatCurrency(ENVASE_TICKET_MONTO_CLP)})
                        </button>
                    )}
                    <div className="flex gap-2 w-full">
                        {/* Generar preventa: solo Preventa (VND obligatorio) y Admin. */}
                        {!activePreventaId && canCreatePreventa && (
                        <div className="flex-1 relative group">
                            <button
                                className="checkout-btn w-full bg-indigo-500 hover:bg-indigo-600"
                                disabled={cart.length === 0 || loading}
                                onClick={() => {
                                    if (cart.length === 0) return;
                                    if (isPreventaPrevendedor) {
                                        setShowVendedorPreventaModal(true);
                                        setVendedorPreventaInput('');
                                        setVendedorPreventaResolved(null);
                                        setTimeout(() => vendedorPreventaInputRef.current?.focus(), 100);
                                    } else {
                                        // Cajero/Admin: preventa atribuida al usuario actual.
                                        doCreatePreventa(null);
                                    }
                                }}
                                title={cart.length === 0 ? 'Agregue productos al carrito primero' : 'Crear una preventa (venta pendiente)'}
                            >
                                <FileText size={20} /> Generar Preventa
                            </button>
                            {cart.length === 0 && (
                                <div className="absolute bottom-full left-0 mb-2 w-56 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg z-50 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                    <div className="font-semibold mb-1">📝 Preventa</div>
                                    <div>Una preventa es una venta pendiente. El cliente puede pagar después en caja usando el código de barras del ticket.</div>
                                    <div className="absolute bottom-0 left-4 transform translate-y-full">
                                        <div className="border-4 border-transparent border-t-gray-900"></div>
                                    </div>
                                </div>
                            )}
                        </div>
                        )}

                        {/* Complete Sale Button - Cajero/Admin siempre, Vendedor solo si tiene preventa cargada */}
                        {canCompleteSaleWithLoadedPreventa && (
                            <div className="flex-1 flex flex-col gap-2">
                                <div className="relative group">
                                    <button
                                        className={`checkout-btn checkout-btn--slim w-full ${
                                            (cart.length === 0 || loading || (requiereCajaOperativa && !puedeEjecutarCobro) || (isVendedorRole && !activePreventaId))
                                                ? 'disabled:bg-gray-400 disabled:cursor-not-allowed opacity-50'
                                                : ''
                                        }`}
                                        disabled={
                                            cart.length === 0 ||
                                            loading ||
                                            (requiereCajaOperativa && !puedeEjecutarCobro) ||
                                            (isVendedorRole && !activePreventaId) ||
                                            (saleRequiresVnd && !selectedVendedor)
                                        }
                                        onClick={() => {
                                            console.log('🛒 Intentando completar venta:', {
                                                cartLength: cart.length,
                                                loading,
                                                isCajero,
                                                cajaSessionIsOpen: cajaSession?.isOpen,
                                                cajaSession,
                                                isVendedorRole,
                                                activePreventaId
                                            });
                                            handleCompleteSale();
                                        }}
                                    >
                                        <DollarSign size={20} /> Completar Venta
                                    </button>
                                    
                                    {/* Tooltip sutil solo al hacer hover cuando está deshabilitado */}
                                    {(cart.length === 0 || (requiereCajaOperativa && !puedeEjecutarCobro) || (isVendedorRole && !activePreventaId) || (saleRequiresVnd && !selectedVendedor)) && (
                                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg z-50 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                            <div className="font-medium mb-1.5">¿Por qué está deshabilitado?</div>
                                            <div className="space-y-1.5">
                                                {cart.length === 0 && (
                                                    <div className="text-gray-300">• Agregue productos al carrito</div>
                                                )}
                                                {requiereCajaOperativa && !cajaSession?.isOpen && (
                                                    <div className="text-gray-300">• Haga clic en "Abrir Caja" para iniciar turno</div>
                                                )}
                                                {requiereCajaOperativa && cajeroVenBloqueadoPorEstadoLocal && (
                                                    <div className="text-gray-300">• Active la sucursal y marque la caja como operativa (Sucursales o Cajas)</div>
                                                )}
                                                {isVendedorRole && !activePreventaId && (
                                                    <div className="text-gray-300">• Debe cargar una preventa</div>
                                                )}
                                                {saleRequiresVnd && !selectedVendedor && (
                                                    <div className="text-gray-300">• Escanee código VND del vendedor</div>
                                                )}
                                            </div>
                                            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
                                                <div className="border-4 border-transparent border-t-gray-900"></div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Mensaje sutil solo cuando es necesario y el botón está deshabilitado */}
                                {isCajero && !cajaSession?.isOpen && !cajeroVenBloqueadoPorEstadoLocal && cart.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => setShowOpenCajaModal(true)}
                                        className="w-full text-xs text-amber-700 bg-amber-100 border border-amber-300 rounded px-3 py-2 flex items-center justify-center gap-2 hover:bg-amber-200 transition-colors font-medium cursor-pointer"
                                    >
                                        <Unlock size={14} />
                                        <span>Haga clic para abrir caja y poder completar la venta</span>
                                    </button>
                                )}
                                {isCajero && cajeroVenBloqueadoPorEstadoLocal && cart.length > 0 && (
                                    <Link
                                        to="/sucursales"
                                        className="w-full text-xs text-red-900 bg-red-50 border border-red-200 rounded px-3 py-2 flex items-center justify-center gap-2 font-semibold hover:bg-red-100"
                                    >
                                        Corregir estado de sucursal / caja
                                    </Link>
                                )}
                                {isVendedorRole && !activePreventaId && cart.length > 0 && (
                                    <div className="w-full text-xs text-amber-600 bg-amber-50/50 border border-amber-200/50 rounded px-2 py-1.5 flex items-center gap-1.5">
                                        <FileText size={12} className="text-amber-600 flex-shrink-0" />
                                        <span>Debe cargar una preventa para completar la venta</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
        </div>
            
            {showOpenCajaModal && canCompleteSale && (
                <div className="fixed inset-0 bg-gray-900/90 backdrop-blur-sm flex items-center justify-center z-50 transition-opacity p-4 overflow-y-auto">
                    <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-2xl w-full max-w-md border border-gray-100 transform scale-100 transition-transform my-4">
                        <div className="flex flex-col items-center mb-6">
                            <div className="bg-indigo-50 p-4 rounded-full mb-4">
                                <Unlock size={32} className="text-indigo-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-800">Abrir Caja</h2>
                            <p className="text-gray-500 text-center mt-2 text-sm">Ingrese el monto y la cantidad de billetes/monedas que tiene la caja registradora al iniciar el turno.</p>
                        </div>

                        <form onSubmit={handleOpenCaja} className="flex flex-col gap-5">
                            <div className="form-group">
                                <label className="text-gray-700 font-semibold mb-1">Seleccionar Caja</label>
                                <select
                                    required
                                    disabled={availableCajas.length === 0}
                                    className="w-full border-gray-300 rounded-lg p-3 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all outline-none disabled:opacity-60"
                                    value={openCajaData.caja_id}
                                    onChange={e => setOpenCajaData({ ...openCajaData, caja_id: e.target.value })}
                                >
                                    <option value="">Seleccione una caja...</option>
                                    {availableCajas.map(c => (
                                        <option key={c.id} value={c.id}>
                                            {c.sucursal_nombre ? `${c.nombre} (${c.sucursal_nombre})` : (c.nombre || `Caja #${c.id}`)}
                                        </option>
                                    ))}
                                </select>
                                {availableCajas.length === 0 && (
                                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-2">
                                        No hay cajas disponibles. Un administrador debe crear cajas en <strong>Cajas</strong> y asignarlas si corresponde. Cierre sesión y vuelva a entrar tras corregir permisos.
                                    </p>
                                )}
                            </div>
                            <div className="form-group">
                                <label className="text-gray-700 font-semibold mb-1">Monto en efectivo en la caja registradora</label>
                                <div className="relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">$</span>
                                    <input
                                        required
                                        type="number"
                                        min="0"
                                        className="w-full border-gray-300 rounded-lg p-3 pl-10 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all outline-none text-lg font-bold text-gray-800"
                                        placeholder="0"
                                        value={openCajaData.monto_inicial}
                                        onChange={e => setOpenCajaData({ ...openCajaData, monto_inicial: e.target.value })}
                                    />
                                </div>
                                <p className="text-xs text-gray-500 mt-1">Conteo físico del dinero al iniciar el turno.</p>
                            </div>
                            <DenominacionEfectivo
                                value={openCajaDesglose}
                                onChange={(next, total) => {
                                    setOpenCajaDesglose(next);
                                    setOpenCajaData((prev) => ({ ...prev, monto_inicial: String(total) }));
                                }}
                                label="Cantidad de billetes y monedas"
                                accentColor="#4f46e5"
                                compact
                                showTotal={false}
                            />
                            <button type="submit" className="w-full bg-indigo-500 text-gray-900 p-4 rounded-xl font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-200 transaction-all mt-2 transform hover:-translate-y-0.5">
                                Abrir Sesión
                            </button>
                        </form>
                    </div>
                </div>
            )}
            {showPesoModal && pesoProductToAdd && (
                <div className="fixed inset-0 bg-gray-900/90 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-sm border border-gray-100">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-gray-800">Ingresar gramos</h2>
                            <button onClick={() => { setShowPesoModal(false); setPesoProductToAdd(null); setPesoGramsInput(''); }} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        <p className="text-gray-600 mb-4">{pesoProductToAdd.nombre}</p>
                        <p className="text-sm text-gray-500 mb-2">
                            Precio: {formatCurrency(pesoProductToAdd.precio_por_kilo || pesoProductToAdd.valor_producto)}/kg
                        </p>
                        <div className="flex gap-2 mb-4">
                            {[100, 250, 500, 1000].map(g => (
                                <button
                                    key={g}
                                    type="button"
                                    onClick={() => setPesoGramsInput(String(g))}
                                    className="flex-1 py-2 px-3 bg-gray-100 hover:bg-indigo-100 hover:text-indigo-700 rounded-lg text-sm font-medium transition-colors"
                                >
                                    {g}g
                                </button>
                            ))}
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Gramos</label>
                            <input
                                type="number"
                                min="1"
                                step="1"
                                value={pesoGramsInput}
                                onChange={e => setPesoGramsInput(e.target.value)}
                                placeholder="Ej: 350"
                                className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                autoFocus
                            />
                        </div>
                        {pesoGramsInput && parseInt(pesoGramsInput, 10) > 0 && (
                            <p className="text-sm text-emerald-600 font-medium mb-4">
                                Total: {formatCurrency(Math.round(((pesoProductToAdd.precio_por_kilo || pesoProductToAdd.valor_producto) / 1000) * parseInt(pesoGramsInput, 10)))}
                            </p>
                        )}
                        <button
                            type="button"
                            onClick={confirmAddPesoToCart}
                            className="w-full bg-indigo-500 text-gray-900 p-3 rounded-xl font-bold hover:bg-indigo-700"
                        >
                            Agregar al carrito
                        </button>
                    </div>
                </div>
            )}
            {showCloseCajaModal && ReactDOM.createPortal(
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 99999,
                        backdropFilter: 'blur(4px)'
                    }}
                >
                    <div
                        style={{
                            backgroundColor: '#ffffff',
                            borderRadius: '16px',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                            width: '90%',
                            maxWidth: '440px',
                            maxHeight: '92vh',
                            overflowY: 'auto',
                            fontFamily: 'system-ui, -apple-system, sans-serif'
                        }}
                    >
                        {/* Header - Explicit Column Layout */}
                        <div style={{
                            backgroundColor: '#fff1f2', // rose-50
                            padding: '24px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            borderBottom: '1px solid #ffe4e6'
                        }}>
                            <div style={{
                                backgroundColor: 'white',
                                padding: '12px',
                                borderRadius: '9999px',
                                marginBottom: '16px',
                                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                            }}>
                                <Lock size={32} color="#e11d48" /> {/* rose-600 */}
                            </div>
                            <h2 style={{
                                fontSize: '20px',
                                fontWeight: '700',
                                color: '#1f2937',
                                margin: '0 0 8px 0',
                                textAlign: 'center'
                            }}>
                                Cerrar Caja
                            </h2>
                            <p style={{
                                fontSize: '14px',
                                color: '#6b7280',
                                textAlign: 'center',
                                margin: 0,
                                lineHeight: '1.5'
                            }}>
                                Ingrese solo el efectivo contado en caja. Transferencias y tarjetas se toman del registro del sistema.
                            </p>
                        </div>

                        {/* Body - Cuadre por tipo de pago */}
                        <div style={{ padding: '24px' }}>
                            {cierreResumen?.corte_turno?.fecha_operacional && (
                                <div style={{
                                    background: '#eef2ff',
                                    border: '1px solid #c7d2fe',
                                    borderRadius: '12px',
                                    padding: '10px 14px',
                                    marginBottom: '12px',
                                    fontSize: '13px',
                                    color: '#3730a3',
                                    fontWeight: 600,
                                }}>
                                    Día operacional:{' '}
                                    {new Date(
                                        cierreResumen.corte_turno.fecha_operacional + 'T12:00:00'
                                    ).toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                </div>
                            )}
                            {cierreResumen && (
                                <div style={{
                                    background: '#f0fdf4',
                                    border: '1px solid #bbf7d0',
                                    borderRadius: '12px',
                                    padding: '12px 16px',
                                    marginBottom: '20px',
                                    fontSize: '13px',
                                    color: '#166534'
                                }}>
                                    <strong>Registro del sistema (transacciones):</strong>
                                    <div style={{ marginTop: '6px', display: 'grid', gap: '4px' }}>
                                        {cierreResumen.cuadre_efectivo && (
                                            <>
                                                <span>Monto inicial: {formatCurrency(cierreResumen.cuadre_efectivo.monto_inicial)}</span>
                                                <span>Efectivo ventas: {formatCurrency(cierreResumen.cuadre_efectivo.efectivo_ventas)}</span>
                                                {Number(cierreResumen.corte_turno?.resumen_cierre?.egresos_vuelto_efectivo) > 0 && (
                                                    <span>− Vuelto en efectivo: {formatCurrency(cierreResumen.corte_turno.resumen_cierre.egresos_vuelto_efectivo)}</span>
                                                )}
                                                {Number(cierreResumen.corte_turno?.resumen_cierre?.egresos_vuelto_tarjeta) > 0 && (
                                                    <span>− Vuelto/giro con tarjeta: {formatCurrency(cierreResumen.corte_turno.resumen_cierre.egresos_vuelto_tarjeta)}</span>
                                                )}
                                                {Number(cierreResumen.corte_turno?.resumen_cierre?.ingresos_inyeccion_efectivo) > 0 && (
                                                    <span>+ Inyección efectivo: {formatCurrency(cierreResumen.corte_turno.resumen_cierre.ingresos_inyeccion_efectivo)}</span>
                                                )}
                                                {Number(cierreResumen.corte_turno?.resumen_cierre?.egresos_retiro_seguridad) > 0 && (
                                                    <span>− Retiro seguridad: {formatCurrency(cierreResumen.corte_turno.resumen_cierre.egresos_retiro_seguridad)}</span>
                                                )}
                                                {Math.abs(Number(cierreResumen.cuadre_efectivo.ajustes_efectivo) || 0) > 0.001 && (
                                                    <span>Ajustes (envases u otros): {formatCurrency(cierreResumen.cuadre_efectivo.ajustes_efectivo)}</span>
                                                )}
                                            </>
                                        )}
                                        <span style={{ marginTop: '4px', fontWeight: 700 }}>Efectivo esperado en caja: {formatCurrency(cierreResumen.efectivo_esperado_en_caja)}</span>
                                    </div>
                                </div>
                            )}
                            {cierreResumen && Number(cierreResumen.efectivo_esperado_en_caja) < 0 && (
                                <div style={{
                                    background: '#fffbeb',
                                    border: '1px solid #fcd34d',
                                    borderRadius: '12px',
                                    padding: '10px 14px',
                                    marginBottom: '16px',
                                    fontSize: '12px',
                                    color: '#92400e',
                                    lineHeight: 1.45,
                                }}>
                                    El sistema calcula un saldo negativo (vueltos/giros superan el efectivo). Ingrese el{' '}
                                    <strong>efectivo real</strong> en el cajón y use «Cerrar con descuadre» si no cuadra.
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={handleImprimirCorteTurno}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    borderRadius: '12px',
                                    backgroundColor: '#ecfdf5',
                                    border: '1px solid #6ee7b7',
                                    color: '#047857',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    marginBottom: '16px',
                                }}
                            >
                                <Printer size={18} /> Imprimir corte de turno (sin cerrar)
                            </button>
                            <form onSubmit={handleCloseCaja} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', textTransform: 'uppercase', color: '#374151', marginBottom: '6px' }}>Efectivo (conteo físico en caja)</label>
                                    <input
                                        type="number"
                                        step="1"
                                        placeholder="0"
                                        value={closeCajaData.efectivo_cierre}
                                        onChange={e => setCloseCajaData({ ...closeCajaData, efectivo_cierre: e.target.value })}
                                        style={{ width: '100%', padding: '12px 16px', fontSize: '16px', border: '1px solid #d1d5db', borderRadius: '12px', boxSizing: 'border-box' }}
                                        autoFocus
                                    />
                                </div>
                                <DenominacionEfectivo
                                    value={closeCajaDesglose}
                                    onChange={(next, total) => {
                                        setCloseCajaDesglose(next);
                                        setCloseCajaData({ efectivo_cierre: String(total) });
                                    }}
                                    label="Cantidad de billetes y monedas"
                                    accentColor="#e11d48"
                                    compact
                                    showTotal={false}
                                />
                                {(() => {
                                    const contado = parseMontoClp(closeCajaData.efectivo_cierre);
                                    const esperado = Number(cierreResumen?.efectivo_esperado_en_caja);
                                    const diff =
                                        contado != null && Number.isFinite(esperado)
                                            ? contado - esperado
                                            : null;
                                    const hayDescuadre = diff != null && Math.abs(diff) > 1;
                                    return (
                                        <>
                                            {hayDescuadre && (
                                                <div style={{
                                                    fontSize: '13px',
                                                    color: '#b45309',
                                                    fontWeight: 600,
                                                    textAlign: 'center',
                                                }}>
                                                    Diferencia vs sistema: {formatCurrency(diff)}
                                                </div>
                                            )}
                                            <label style={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: '10px',
                                                fontSize: '13px',
                                                color: '#374151',
                                                cursor: 'pointer',
                                                padding: '10px 12px',
                                                background: closeCajaForzar ? '#fff1f2' : '#f9fafb',
                                                border: `1px solid ${closeCajaForzar ? '#fda4af' : '#e5e7eb'}`,
                                                borderRadius: '12px',
                                            }}>
                                                <input
                                                    type="checkbox"
                                                    checked={closeCajaForzar}
                                                    onChange={e => setCloseCajaForzar(e.target.checked)}
                                                    style={{ marginTop: '2px' }}
                                                />
                                                <span>
                                                    <strong>Cerrar con descuadre</strong> — omitir validación de cuadre y registrar la diferencia en el corte.
                                                </span>
                                            </label>
                                        </>
                                    );
                                })()}

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                    <button
                                        type="button"
                                        onClick={() => setShowCloseCajaModal(false)}
                                        style={{
                                            padding: '12px',
                                            borderRadius: '12px',
                                            backgroundColor: 'white',
                                            border: '1px solid #d1d5db',
                                            color: '#374151',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            fontSize: '15px'
                                        }}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        style={{
                                            padding: '12px',
                                            borderRadius: '12px',
                                            backgroundColor: '#e11d48',
                                            border: 'none',
                                            color: 'white',
                                            fontWeight: '700',
                                            cursor: 'pointer',
                                            fontSize: '15px',
                                            boxShadow: '0 4px 6px -1px rgba(225, 29, 72, 0.3)'
                                        }}
                                    >
                                        Cerrar Turno
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {showPagoCuentaModal && ReactDOM.createPortal(
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 99999,
                        backdropFilter: 'blur(4px)',
                    }}
                    onClick={() => !pagoCuentaLoading && setShowPagoCuentaModal(false)}
                >
                    <div
                        style={{
                            backgroundColor: '#ffffff',
                            borderRadius: '16px',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                            width: '90%',
                            maxWidth: '400px',
                            padding: '24px',
                            fontFamily: 'system-ui, -apple-system, sans-serif',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0c4a6e' }}>
                                Pago de cuenta / servicio
                            </h2>
                            <button
                                type="button"
                                onClick={() => setShowPagoCuentaModal(false)}
                                disabled={pagoCuentaLoading}
                                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#6b7280' }}
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
                            <button
                                type="button"
                                onClick={() => setPagoCuentaModo('efectivo')}
                                style={{
                                    flex: 1,
                                    padding: '8px 10px',
                                    borderRadius: '8px',
                                    border: pagoCuentaModo === 'efectivo' ? '2px solid #0284c7' : '1px solid #d1d5db',
                                    background: pagoCuentaModo === 'efectivo' ? '#e0f2fe' : '#fff',
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    cursor: 'pointer',
                                }}
                            >
                                Efectivo en caja
                            </button>
                            <button
                                type="button"
                                onClick={() => setPagoCuentaModo('tarjeta_externa')}
                                style={{
                                    flex: 1,
                                    padding: '8px 10px',
                                    borderRadius: '8px',
                                    border: pagoCuentaModo === 'tarjeta_externa' ? '2px solid #0284c7' : '1px solid #d1d5db',
                                    background: pagoCuentaModo === 'tarjeta_externa' ? '#e0f2fe' : '#fff',
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    cursor: 'pointer',
                                }}
                            >
                                Tarjeta directa
                            </button>
                        </div>
                        <p style={{ fontSize: '0.85rem', color: '#475569', margin: '0 0 16px', lineHeight: 1.45 }}>
                            {pagoCuentaModo === 'efectivo' ? (
                                <>
                                    El cliente paga una cuenta en <strong>efectivo</strong> en caja. Entra al arqueo físico pero{' '}
                                    <strong>no es venta de productos</strong>.
                                </>
                            ) : (
                                <>
                                    El cliente pagó con <strong>tarjeta directo a la entidad</strong> (Recauda, etc.). Solo registro
                                    informativo: <strong>no afecta</strong> caja física ni tarjeta digitada en el sistema.
                                </>
                            )}
                        </p>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                            {pagoCuentaModo === 'efectivo' ? 'Monto recibido en efectivo' : 'Monto pagado con tarjeta externa'}
                        </label>
                        <div style={{ position: 'relative', marginBottom: '16px' }}>
                            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontWeight: 700 }}>$</span>
                            <input
                                type="number"
                                min="1"
                                value={pagoCuentaMonto}
                                onChange={(e) => setPagoCuentaMonto(e.target.value)}
                                placeholder="0"
                                autoFocus
                                style={{
                                    width: '100%',
                                    padding: '10px 10px 10px 28px',
                                    fontSize: '18px',
                                    fontWeight: 700,
                                    border: '1px solid #d1d5db',
                                    borderRadius: '10px',
                                    boxSizing: 'border-box',
                                }}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                type="button"
                                onClick={() => setShowPagoCuentaModal(false)}
                                disabled={pagoCuentaLoading}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    borderRadius: '10px',
                                    border: '1px solid #d1d5db',
                                    background: '#fff',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={handlePagoCuenta}
                                disabled={pagoCuentaLoading}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    borderRadius: '10px',
                                    border: 'none',
                                    background: '#0284c7',
                                    color: '#fff',
                                    fontWeight: 700,
                                    cursor: pagoCuentaLoading ? 'wait' : 'pointer',
                                }}
                            >
                                {pagoCuentaLoading ? 'Registrando…' : 'Registrar'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {showMovimientoEfectivoModal && ReactDOM.createPortal(
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 99999,
                        backdropFilter: 'blur(4px)',
                        padding: '16px',
                        overflowY: 'auto',
                    }}
                    onClick={() => {
                        if (!movimientoEfectivoLoading) {
                            setShowMovimientoEfectivoModal(false);
                            setMovimientoEfectivoSugerido(null);
                        }
                    }}
                >
                    <div
                        style={{
                            backgroundColor: '#ffffff',
                            borderRadius: '16px',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                            width: '90%',
                            maxWidth: '440px',
                            maxHeight: '92vh',
                            overflowY: 'auto',
                            padding: '24px',
                            fontFamily: 'system-ui, -apple-system, sans-serif',
                            margin: 'auto',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: movimientoEfectivoTipo === 'inyeccion_efectivo' ? '#047857' : '#b45309' }}>
                                {movimientoEfectivoTipo === 'inyeccion_efectivo' ? 'Inyección de efectivo' : 'Retiro por seguridad'}
                            </h2>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowMovimientoEfectivoModal(false);
                                    setMovimientoEfectivoSugerido(null);
                                }}
                                disabled={movimientoEfectivoLoading}
                                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#6b7280' }}
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <p style={{ fontSize: '0.85rem', color: '#475569', margin: '0 0 12px', lineHeight: 1.45 }}>
                            {movimientoEfectivoTipo === 'inyeccion_efectivo' ? (
                                <>
                                    Agrega billetes al cajón durante el turno (fondo de cambio, reposición).{' '}
                                    <strong>No es venta</strong>; suma al efectivo esperado en el arqueo.
                                </>
                            ) : (
                                <>
                                    Retira efectivo del cajón hacia bóveda o caja fuerte por seguridad.{' '}
                                    <strong>Reduce</strong> el efectivo esperado en el arqueo; no es gasto del negocio.
                                </>
                            )}
                        </p>
                        {movimientoEfectivoSugerido != null && (
                            <p style={{
                                fontSize: '0.8rem',
                                color: '#047857',
                                background: '#ecfdf5',
                                border: '1px solid #a7f3d0',
                                borderRadius: 10,
                                padding: '8px 10px',
                                margin: '0 0 12px',
                            }}>
                                Sugerido para cubrir vuelto: <strong>{formatCurrency(movimientoEfectivoSugerido)}</strong>
                            </p>
                        )}
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                            Monto en efectivo
                        </label>
                        <div style={{ position: 'relative', marginBottom: '12px' }}>
                            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontWeight: 700 }}>$</span>
                            <input
                                type="number"
                                min="1"
                                value={movimientoEfectivoMonto}
                                onChange={(e) => setMovimientoEfectivoMonto(e.target.value)}
                                placeholder="0"
                                autoFocus
                                disabled={movimientoEfectivoLoading}
                                style={{
                                    width: '100%',
                                    padding: '10px 10px 10px 28px',
                                    fontSize: '18px',
                                    fontWeight: 700,
                                    border: '1px solid #d1d5db',
                                    borderRadius: '10px',
                                    boxSizing: 'border-box',
                                }}
                            />
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <DenominacionEfectivo
                                value={movimientoEfectivoDesglose}
                                onChange={(next, total) => {
                                    setMovimientoEfectivoDesglose(next);
                                    setMovimientoEfectivoMonto(String(total));
                                }}
                                label="Cantidad de billetes y monedas"
                                accentColor={movimientoEfectivoTipo === 'inyeccion_efectivo' ? '#059669' : '#d97706'}
                                disabled={movimientoEfectivoLoading}
                                compact
                                showTotal={false}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowMovimientoEfectivoModal(false);
                                    setMovimientoEfectivoSugerido(null);
                                }}
                                disabled={movimientoEfectivoLoading}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    borderRadius: '10px',
                                    border: '1px solid #d1d5db',
                                    background: '#fff',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={handleMovimientoEfectivo}
                                disabled={movimientoEfectivoLoading || (parseMontoClp(movimientoEfectivoMonto) ?? 0) <= 0}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    borderRadius: '10px',
                                    border: 'none',
                                    background: movimientoEfectivoTipo === 'inyeccion_efectivo' ? '#059669' : '#d97706',
                                    color: '#fff',
                                    fontWeight: 700,
                                    cursor: movimientoEfectivoLoading ? 'wait' : 'pointer',
                                    opacity: (parseMontoClp(movimientoEfectivoMonto) ?? 0) <= 0 ? 0.5 : 1,
                                }}
                            >
                                {movimientoEfectivoLoading ? 'Registrando…' : 'Registrar'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Modal: Escanear vendedor antes de crear preventa */}
            {showVendedorPreventaModal && (
                <div className="modal-overlay">
                    <div className="modal-content max-w-md">
                        <button onClick={() => setShowVendedorPreventaModal(false)} className="modal-close"><X size={20} /></button>
                        <h2 className="modal-title">Preventa — código del vendedor</h2>
                        <p className="text-gray-600 text-sm mb-4">
                            Escanee o escriba el código de barras del vendedor (VND...) que atendió la preventa. Sin ese código no se puede crear el ticket.
                        </p>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Código del vendedor (VND...)</label>
                                <input
                                    ref={vendedorPreventaInputRef}
                                    type="text"
                                    value={vendedorPreventaInput}
                                    onChange={e => setVendedorPreventaInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && vendedorPreventaResolved && !loading) {
                                            e.preventDefault();
                                            doCreatePreventa(vendedorPreventaResolved);
                                        }
                                    }}
                                    placeholder="Escanee o escriba VND + ID"
                                    className="w-full border border-gray-300 rounded-lg p-3 text-lg font-mono focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                    autoFocus
                                />
                                {vendedorPreventaResolved && (
                                    <p className="mt-2 text-sm text-green-600 font-medium">✓ Vendedor: {vendedorPreventaResolved.name}</p>
                                )}
                                {vendedorPreventaInput.trim().startsWith('VND') && vendedorPreventaInput.length >= 4 && !vendedorPreventaResolved && (
                                    <p className="mt-2 text-sm text-amber-600">Validando...</p>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => vendedorPreventaResolved && doCreatePreventa(vendedorPreventaResolved)}
                                disabled={!vendedorPreventaResolved || loading}
                                className="w-full px-4 py-3 rounded-lg bg-indigo-500 text-gray-900 hover:bg-indigo-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {vendedorPreventaResolved ? `Crear preventa e imprimir (${vendedorPreventaResolved.name})` : 'Escanee un código VND válido para continuar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Preventa List Modal */}
            {showPreventaModal && (
                <div className="modal-overlay">
                    <div className="modal-content max-w-2xl">
                        <button onClick={() => setShowPreventaModal(false)} className="modal-close"><X size={20} /></button>
                        <h2 className="modal-title">Preventas Pendientes</h2>
                        <div className="overflow-y-auto max-h-[400px]">
                            {preventas.length === 0 ? (
                                <p className="text-center py-8 text-gray-500">No hay preventas pendientes.</p>
                            ) : (
                                <table className="w-full text-left report-table">
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>Vendedor</th>
                                            <th>Items</th>
                                            <th>Total</th>
                                            <th>Hora</th>
                                            <th>Acción</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preventas.map(p => (
                                            <tr key={p.id}>
                                                <td>#{p.id}</td>
                                                <td>{p.vendedor_name || 'Unknown'}</td>
                                                <td>{p.cantidad_productos}</td>
                                                <td className="font-bold">{formatCurrency(p.sub_total)}</td>
                                                <td className="text-xs text-gray-500">{new Date(p.created_at).toLocaleTimeString()}</td>
                                                <td>
                                                    <button
                                                        onClick={() => loadPreventa(p.id)}
                                                        className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100 text-sm"
                                                    >
                                                        Cargar
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de selección de opción de promoción (cuando tiene varias opciones) */}
            {promoSelectionModal && (
                <div className="modal-overlay">
                    <div className="modal-content max-w-lg">
                        <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                            <h2 className="modal-title mb-0">Seleccione opción</h2>
                            <button
                                onClick={() => setPromoSelectionModal(null)}
                                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <p className="text-gray-600 text-sm mb-4">
                            {promoSelectionModal.promo.nombre} — Elija la opción con stock disponible:
                        </p>
                        {promoSelectionModal.allHaveStock && (
                            <button
                                type="button"
                                onClick={() => addPromoToCart(promoSelectionModal.promo, null, true)}
                                className="w-full mb-4 p-3 rounded-xl border-2 border-green-300 bg-green-50 text-green-800 font-semibold hover:bg-green-100 transition-colors"
                            >
                                Agregar pack completo (todos los productos)
                            </button>
                        )}
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                            {promoSelectionModal.detail.items?.map(item => {
                                const stock = parseInt(item.stock_actual) || 0;
                                const cantidad = parseInt(item.cantidad) || 1;
                                const hasStock = stock >= cantidad;
                                return (
                                    <button
                                        key={item.producto_id}
                                        type="button"
                                        disabled={!hasStock}
                                        onClick={() => addPromoToCart(promoSelectionModal.promo, { producto_id: item.producto_id, cantidad })}
                                        className={`w-full text-left p-4 rounded-xl border-2 transition-all flex justify-between items-center ${
                                            hasStock 
                                                ? 'border-amber-200 bg-amber-50/50 hover:border-amber-400 hover:bg-amber-100 cursor-pointer' 
                                                : 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
                                        }`}
                                    >
                                        <div>
                                            <span className="font-medium text-gray-900">{item.nombre}</span>
                                            <span className="text-gray-500 text-sm ml-2">× {cantidad}</span>
                                        </div>
                                        <div className={`text-sm font-bold ${hasStock ? 'text-green-600' : 'text-red-600'}`}>
                                            Stock: {stock}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Promociones Modal - dentro del POS, no pierde el carrito */}
            {showPromocionesModal && (
                <div className="modal-overlay">
                    <div className="modal-content max-w-2xl max-h-[85vh] flex flex-col">
                        <div className="flex items-center gap-3 pb-4 border-b border-gray-200 flex-shrink-0">
                            <button
                                onClick={() => { setShowPromocionesModal(false); setPromoSearchTerm(''); }}
                                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-800 transition-colors cursor-pointer"
                                title="Volver al punto de venta (mantiene el carrito)"
                            >
                                <ArrowLeft size={20} />
                            </button>
                            <h2 className="modal-title mb-0 flex-1">Promociones</h2>
                        </div>
                        <div className="relative mb-4 flex-shrink-0">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                            <input
                                type="text"
                                placeholder="Buscar por nombre..."
                                value={promoSearchTerm}
                                onChange={e => setPromoSearchTerm(e.target.value)}
                                className="w-full pl-14 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                            />
                        </div>
                        <div className="overflow-y-auto flex-1 min-h-0">
                            {filteredPromotions.length === 0 ? (
                                <p className="text-center py-8 text-gray-500">
                                    {promoSearchTerm ? 'No se encontraron promociones' : 'No hay promociones activas'}
                                </p>
                            ) : (
                                <div className="space-y-3">
                                    {filteredPromotions.map(promo => (
                                        <div
                                            key={promo.id}
                                            className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl hover:border-amber-300 hover:shadow-md transition-all cursor-pointer group"
                                            onClick={() => addPromoToCart(promo)}
                                        >
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-bold text-gray-900">{promo.nombre}</h3>
                                                {promo.descripcion && (
                                                    <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{promo.descripcion}</p>
                                                )}
                                                <span className="inline-block mt-2 text-lg font-bold text-amber-600">
                                                    {formatCurrency(promo.monto)}
                                                </span>
                                            </div>
                                            <div className="flex-shrink-0 ml-4">
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); addPromoToCart(promo); }}
                                                    className="p-2 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors opacity-0 group-hover:opacity-100"
                                                    title="Agregar al carrito"
                                                >
                                                    <Plus size={20} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <p className="text-xs text-gray-400 mt-3 flex-shrink-0">
                            Haga clic en una promoción para agregarla al carrito. Use la flecha para volver sin perder el carrito.
                        </p>
                    </div>
                </div>
            )}
            {showPaymentModal && ReactDOM.createPortal(
                <div
                    role="presentation"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 999999,
                        backgroundColor: 'rgba(0, 0, 0, 0.55)',
                        backdropFilter: 'blur(4px)',
                        overflowY: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        padding: '12px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        boxSizing: 'border-box',
                    }}
                    onMouseDown={(e) => {
                        if (e.target === e.currentTarget) setShowPaymentModal(false);
                    }}
                >
                    <div
                        role="dialog"
                        aria-labelledby="payment-modal-title"
                        style={{
                        backgroundColor: 'white',
                        borderRadius: '16px',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        width: '100%',
                        maxWidth: '520px',
                        maxHeight: 'min(calc(100dvh - 24px), 92vh)',
                        overflowY: 'auto',
                        margin: 'auto',
                        flexShrink: 0,
                        fontFamily: 'system-ui, -apple-system, sans-serif'
                    }}>
                        {/* Header */}
                        <div style={{
                            backgroundColor: '#10b981',
                            padding: '14px 16px',
                            color: 'white',
                            textAlign: 'center'
                        }}>
                            <h2 id="payment-modal-title" style={{ fontSize: '17px', fontWeight: 'bold', margin: 0 }}>Total a pagar</h2>
                            <p style={{ fontSize: '28px', fontWeight: '800', margin: '6px 0 0 0' }}>{formatCurrency(total)}</p>
                            {opcionesSesionCajaVentasActivas && (boletaCargoEnvase || boletaDescuentoDevolucionEnvase) && (
                                <div
                                    style={{
                                        marginTop: '14px',
                                        paddingTop: '12px',
                                        borderTop: '1px solid rgba(255,255,255,0.35)',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        textAlign: 'left',
                                        lineHeight: 1.5,
                                    }}
                                >
                                    <div style={{ opacity: 0.95 }}>Productos: {formatCurrency(calculateCartSubtotal())}</div>
                                    {boletaCargoEnvase && (
                                        <div style={{ opacity: 0.95 }}>+ Ticket envases: {formatCurrency(ENVASE_TICKET_MONTO_CLP)}</div>
                                    )}
                                    {boletaDescuentoDevolucionEnvase && (
                                        <div style={{ opacity: 0.95 }}>− Devolución envase: {formatCurrency(ENVASE_TICKET_MONTO_CLP)}</div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Body */}
                        <div style={{ padding: '16px 18px 18px', boxSizing: 'border-box' }}>
                            {opcionesSesionCajaVentasActivas && (
                                <div
                                    className="w-full rounded-lg border border-amber-200 bg-amber-50/90 px-2.5 py-2 mb-3 space-y-1.5"
                                    style={{ boxSizing: 'border-box' }}
                                >
                                    <label className="flex cursor-pointer items-start gap-2 text-xs font-semibold text-amber-950 leading-tight">
                                        <input
                                            type="checkbox"
                                            className="mt-0.5 shrink-0 rounded border-amber-400 text-amber-700 focus:ring-amber-500"
                                            checked={boletaCargoEnvase}
                                            onChange={(e) => setBoletaCargoEnvase(e.target.checked)}
                                        />
                                        <span>+ Cargo envase en boleta ({formatCurrency(ENVASE_TICKET_MONTO_CLP)})</span>
                                    </label>
                                    <label className="flex cursor-pointer items-start gap-2 text-xs font-semibold text-rose-900 leading-tight">
                                        <input
                                            type="checkbox"
                                            className="mt-0.5 shrink-0 rounded border-rose-300 text-rose-700 focus:ring-rose-500"
                                            checked={boletaDescuentoDevolucionEnvase}
                                            onChange={(e) => setBoletaDescuentoDevolucionEnvase(e.target.checked)}
                                        />
                                        <span>− Devolución envase ({formatCurrency(ENVASE_TICKET_MONTO_CLP)})</span>
                                    </label>
                                    <p className="text-[11px] leading-snug text-amber-900/75 m-0">
                                        Orientativo si ya está en el total: evite duplicar con otro cobro de envase.
                                    </p>
                                    <button
                                        type="button"
                                        disabled={loading}
                                        onClick={printEnvasesTicket}
                                        className="w-full flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-semibold bg-amber-100 text-amber-950 border border-amber-300 hover:bg-amber-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        title={`Papel orientativo. Detalle: no duplicar si el envase ya va en la boleta.`}
                                    >
                                        <Ticket size={15} strokeWidth={2.25} />
                                        Imprimir ticket envases ({formatCurrency(ENVASE_TICKET_MONTO_CLP)})
                                    </button>
                                </div>
                            )}
                            <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 12px', textAlign: 'center' }}>
                                Montos por método — puede combinar.
                            </p>
                            {requiereCajaOperativa && cajaSession?.isOpen && Number.isFinite(Number(cajaSession.efectivo_disponible_en_caja)) && (
                                <div style={{
                                    background: '#ecfdf5',
                                    border: '1px solid #6ee7b7',
                                    borderRadius: '10px',
                                    padding: '8px 12px',
                                    marginBottom: '12px',
                                    fontSize: '12px',
                                    color: '#047857',
                                    fontWeight: 600,
                                    textAlign: 'center',
                                }}>
                                    Efectivo en caja: {formatCurrency(cajaSession.efectivo_disponible_en_caja)}
                                </div>
                            )}
                            {chequeoVueltoEnPago && chequeoVueltoEnPago.falta > 0 && (
                                    <div style={{
                                        background: '#fef2f2',
                                        border: '2px solid #fca5a5',
                                        borderRadius: '10px',
                                        padding: '12px',
                                        marginBottom: '12px',
                                    }}>
                                        <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 700, color: '#b91c1c' }}>
                                            Efectivo insuficiente para el vuelto
                                        </p>
                                        <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#7f1d1d', lineHeight: 1.45 }}>
                                            Disponible {formatCurrency(chequeoVueltoEnPago.efectivoDisponible)}
                                            {chequeoVueltoEnPago.efectivoPagado > 0
                                                ? ` + ${formatCurrency(chequeoVueltoEnPago.efectivoPagado)} recibido`
                                                : ''}
                                            {' '}no alcanza para vuelto de {formatCurrency(chequeoVueltoEnPago.vueltoTotal)}.
                                            Faltan {formatCurrency(chequeoVueltoEnPago.falta)}.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => abrirInyeccionDesdePago(chequeoVueltoEnPago.falta)}
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                borderRadius: '8px',
                                                border: 'none',
                                                background: '#059669',
                                                color: 'white',
                                                fontWeight: 700,
                                                fontSize: '13px',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            Inyectar efectivo ({formatCurrency(chequeoVueltoEnPago.falta)})
                                        </button>
                                    </div>
                                )}

                            {/* Payment methods: tres columnas compactas cuando cabe */}
                            <div className="grid grid-cols-3 max-[440px]:grid-cols-1 gap-2 mb-3">
                                {/* Efectivo */}
                                <div style={{ backgroundColor: '#f0fdf4', border: '2px solid #86efac', borderRadius: '10px', padding: '8px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '700', color: '#166534', marginBottom: '6px', fontSize: '12px' }}>
                                        <DollarSign size={16} /> Efectivo
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontWeight: 'bold' }}>$</span>
                                        <input
                                            type="number"
                                            min="0"
                                            value={paymentData.efectivo || ''}
                                            onChange={(e) => setPaymentData({ ...paymentData, efectivo: Number(e.target.value) || 0 })}
                                            placeholder="0"
                                            style={{
                                                width: '100%',
                                                padding: '8px 8px 8px 26px',
                                                fontSize: '16px',
                                                fontWeight: 'bold',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '8px',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Tarjeta Débito */}
                                <div style={{ backgroundColor: '#eff6ff', border: '2px solid #93c5fd', borderRadius: '10px', padding: '8px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '700', color: '#1e40af', marginBottom: '6px', fontSize: '12px' }}>
                                        <div style={{ width: '18px', height: '12px', backgroundColor: '#1e40af', borderRadius: '2px' }}></div> T. Débito
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontWeight: 'bold' }}>$</span>
                                        <input
                                            type="number"
                                            min="0"
                                            value={paymentData.tarjeta_debito || ''}
                                            onChange={(e) => setPaymentData({ ...paymentData, tarjeta_debito: Number(e.target.value) || 0 })}
                                            placeholder="0"
                                            style={{
                                                width: '100%',
                                                padding: '8px 8px 8px 26px',
                                                fontSize: '16px',
                                                fontWeight: 'bold',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '8px',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Tarjeta Crédito */}
                                <div style={{ backgroundColor: '#fdf4ff', border: '2px solid #e879f9', borderRadius: '10px', padding: '8px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '700', color: '#86198f', marginBottom: '6px', fontSize: '12px' }}>
                                        <div style={{ width: '18px', height: '12px', backgroundColor: '#86198f', borderRadius: '2px' }}></div> T. Crédito
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontWeight: 'bold' }}>$</span>
                                        <input
                                            type="number"
                                            min="0"
                                            value={paymentData.tarjeta_credito || ''}
                                            onChange={(e) => setPaymentData({ ...paymentData, tarjeta_credito: Number(e.target.value) || 0 })}
                                            placeholder="0"
                                            style={{
                                                width: '100%',
                                                padding: '8px 8px 8px 26px',
                                                fontSize: '16px',
                                                fontWeight: 'bold',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '8px',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {isCajero && cajeroPuedeEjecutarCobro && (
                                <div style={{
                                    backgroundColor: '#fffbeb',
                                    border: '2px solid #fcd34d',
                                    borderRadius: '10px',
                                    padding: '10px 12px',
                                    marginBottom: '12px'
                                }}>
                                    <label style={{ display: 'block', fontWeight: '700', color: '#92400e', marginBottom: '4px', fontSize: '13px' }}>
                                        Vuelto en efectivo (caja)
                                    </label>
                                    <p style={{ fontSize: '11px', color: '#78350f', margin: '0 0 8px', lineHeight: 1.35 }}>
                                        Efectivo devuelto al cliente para cuadrar cierre. Solo si venta ≥ $1.000.
                                    </p>
                                    <div style={{ position: 'relative', marginBottom: '8px' }}>
                                        <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontWeight: 'bold' }}>$</span>
                                        <input
                                            type="number"
                                            min="0"
                                            value={vueltoEfectivo || ''}
                                            onChange={(e) => setVueltoEfectivo(Number(e.target.value) || 0)}
                                            placeholder="0"
                                            style={{
                                                width: '100%',
                                                padding: '8px 8px 8px 26px',
                                                fontSize: '16px',
                                                fontWeight: 'bold',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '8px',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setVueltoEfectivo(Math.max(0, Math.round(getTotalPaid() - total)))}
                                        disabled={getTotalPaid() < total}
                                        style={{
                                            padding: '6px 10px',
                                            borderRadius: '8px',
                                            border: '1px solid #d97706',
                                            backgroundColor: getTotalPaid() < total ? '#f3f4f6' : '#fff',
                                            color: getTotalPaid() < total ? '#9ca3af' : '#92400e',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            cursor: getTotalPaid() < total ? 'not-allowed' : 'pointer'
                                        }}
                                        title="Rellena con cobrado − total cuando el cliente paga de más"
                                    >
                                        Sugerido: {formatCurrency(Math.max(0, getTotalPaid() - total))}
                                    </button>
                                </div>
                            )}

                            {/* Summary */}
                            <div style={{ backgroundColor: '#f9fafb', padding: '12px 14px', borderRadius: '10px', marginBottom: '14px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <span style={{ color: '#6b7280' }}>Total a pagar:</span>
                                    <span style={{ fontWeight: 'bold' }}>{formatCurrency(total)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <span style={{ color: '#6b7280' }}>Total ingresado:</span>
                                    <span style={{ fontWeight: 'bold', color: getTotalPaid() >= total ? '#10b981' : '#ef4444' }}>
                                        {formatCurrency(getTotalPaid())}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '2px dashed #d1d5db' }}>
                                    <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
                                        {getTotalPaid() >= total
                                            ? (isCajero && cajeroPuedeEjecutarCobro && vueltoEfectivo > 0
                                                ? 'Vuelto desde caja:'
                                                : 'Vuelto al cliente:')
                                            : 'Falta:'}
                                    </span>
                                    <span style={{ 
                                        fontWeight: '900', 
                                        fontSize: '20px',
                                        color: getTotalPaid() >= total ? '#10b981' : '#ef4444'
                                    }}>
                                        {getTotalPaid() >= total
                                            ? formatCurrency(
                                                isCajero && cajeroPuedeEjecutarCobro && vueltoEfectivo > 0
                                                    ? vueltoEfectivo
                                                    : Math.abs(getTotalPaid() - total)
                                              )
                                            : formatCurrency(Math.abs(getTotalPaid() - total))}
                                    </span>
                                </div>
                            </div>

                            {/* Quick buttons for cash */}
                            {paymentData.efectivo > 0 && (
                                <div style={{ marginBottom: '16px' }}>
                                    <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Montos rápidos efectivo:</p>
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                        {[1000, 2000, 5000, 10000, 20000].map(amount => (
                                            <button
                                                key={amount}
                                                onClick={() => setPaymentData({ ...paymentData, efectivo: amount })}
                                                style={{
                                                    padding: '6px 12px',
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '6px',
                                                    backgroundColor: 'white',
                                                    cursor: 'pointer',
                                                    fontSize: '13px'
                                                }}
                                            >
                                                {formatCurrency(amount)}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <button
                                    onClick={() => setShowPaymentModal(false)}
                                    style={{
                                        padding: '14px',
                                        borderRadius: '12px',
                                        border: '1px solid #d1d5db',
                                        backgroundColor: 'white',
                                        color: '#374151',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={confirmSale}
                                    disabled={getTotalPaid() < total || faltaEfectivoParaVuelto}
                                    style={{
                                        padding: '14px',
                                        borderRadius: '12px',
                                        border: 'none',
                                        backgroundColor: getTotalPaid() < total || faltaEfectivoParaVuelto ? '#9ca3af' : '#10b981',
                                        color: 'white',
                                        fontSize: '16px',
                                        fontWeight: 'bold',
                                        cursor: getTotalPaid() < total || faltaEfectivoParaVuelto ? 'not-allowed' : 'pointer',
                                        boxShadow: getTotalPaid() >= total && !faltaEfectivoParaVuelto ? '0 4px 6px -1px rgba(16, 185, 129, 0.4)' : 'none'
                                    }}
                                >
                                    Confirmar Pago
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}
